/**
 * Call Store — Zustand store for voice call state management.
 *
 * Central hub connecting UI components ↔ TwilioVoiceHandler ↔ TwilioModule.
 * Handles call lifecycle, device state, caller IDs, call history, and UI state.
 */

import { create } from 'zustand';
import { toast } from 'sonner';
import { CALL_STATUS_CONFIGS, isLiveCallStatus } from '@/types/voice';
import type {
  ActiveCall,
  CallStatus,
  CallHistoryItem,
  CallRecording,
  CallerID,
  VoiceNumber,
  SoftphoneState,
  VoiceEvent,
  RingGroup,
} from '@/types/voice';
import { TwilioVoiceHandler } from '@/modules/tpa/twilio/twilio-voice-handler';
import { TwilioAPI } from '@/modules/tpa/twilio/twilio-api';
import type { TwilioCallResource, TwilioRecordingResource, WhatsAppCallPermissionStatus } from '@/modules/tpa/twilio/twilio-types';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useAuthStore, getEffectiveUid } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { toDialE164 } from '@/lib/utils/phone';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/mobile';
import { callFunction } from '@/lib/firebase/functions';
import { doc, getDoc, getDocFromServer, setDoc, getDocs, getDocsFromServer, getCountFromServer, query, where, collection, deleteDoc } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import { getStorageItem, setStorageItem, removeStorageItem } from '@/lib/utils/storage';
import { ref, onChildAdded, onChildChanged, off, type Unsubscribe } from 'firebase/database';
import { rtdb } from '@/config/firebase';

/** 24 hours in milliseconds */
const VOICE_NUMBERS_CACHE_TTL = 24 * 60 * 60 * 1000;

/** Statuses a call can never leave — used to stop in-flight async work from resurrecting a dead call */
// 'answered-elsewhere' belongs here: it is a finished state, reached by
// RECLASSIFYING a call already marked 'canceled' once the sibling-leg check
// confirms a colleague took it. The guard below only blocks resurrecting a
// finished call with a LIVE status, so leaving it out silently pinned those
// calls at 'canceled' and every losing agent saw a false missed call.
const TERMINAL_CALL_STATUSES: CallStatus[] = ['ended', 'canceled', 'rejected', 'ignored', 'error', 'answered-elsewhere'];

/** A caller ID Twilio accepts as `From` for a new leg: no 'whatsapp:' prefix,
 *  never a 'client:' identity (same rules as transferCall). */
function toDialableCallerId(value: string): string {
  const v = (value || '').replace(/^whatsapp:/, '');
  return v && !/^client:/i.test(v) ? v : '';
}

/** A typed number → E.164. A '+' number just loses its spaces and dashes
 *  ("+91 63699 34665" → "+916369934665"). A number without '+' is tried first
 *  as a national number in the caller ID's country ("9876543210"), then as
 *  international with the '+' left off ("919876543210"). Returned unchanged
 *  when neither is valid, so Twilio Lookup still has the final say. */
function resolveBareNumber(raw: string, country?: CountryCode): string {
  if (!raw) return raw;
  if (raw.startsWith('+')) return `+${raw.replace(/\D/g, '')}`;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;
  const national = country ? parsePhoneNumberFromString(digits, country) : undefined;
  if (national?.isValid()) return national.number;
  const international = parsePhoneNumberFromString(`+${digits}`);
  if (international?.isValid()) return international.number;
  return raw;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Calls whose conference room is being watched for "everyone else left" */
const conferenceWatchers = new Set<string>();

/** True when the value is a fetchable http(s) URL (what Twilio needs for waitUrl/HoldUrl) */
export function isValidAudioUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

interface VoiceNumbersCache {
  timestamp: number;
  allVoiceNumbers: VoiceNumber[];
  whatsAppVoiceNumbers: VoiceNumber[];
}

interface CallStore {
  // ── Device state ────────────────────────────────────────────────
  softphoneState: SoftphoneState;
  isAvailable: boolean;
  /** True while waiting for the initial page-load delay before voice init */
  voiceInitWaiting: boolean;
  /** True while an availability toggle's Cloud Function round trip is in flight */
  availabilityUpdating: boolean;
  /** User-facing error message (e.g. when shared user lacks admin config) */
  voiceError: string | null;

  // ── Active calls ────────────────────────────────────────────────
  activeCalls: ActiveCall[];

  // ── Missed calls (inbound calls that were canceled/missed) ─────
  missedCalls: ActiveCall[];

  // ── Caller IDs ──────────────────────────────────────────────────
  callerIds: CallerID[];
  selectedCallerId: string | null;
  /** All voice-capable numbers (linked + unlinked) for the link/unlink UI */
  allVoiceNumbers: VoiceNumber[];
  /** SID currently being linked/unlinked (for loading state) */
  linkingNumberSid: string | null;

  // ── WhatsApp voice calling ───────────────────────────────────────
  /** WhatsApp senders available for voice calling (linked + unlinked) */
  whatsAppVoiceNumbers: VoiceNumber[];
  /** SID currently being linked/unlinked for WhatsApp (for loading state) */
  linkingWhatsAppSenderSid: string | null;
  /** Tracks outbound call permission status per contact number */
  whatsAppCallPermissions: Record<string, WhatsAppCallPermissionStatus>;
  /** Cached SID of the VOICE_CALL_REQUEST content template (avoids re-fetching) */
  _cachedCallRequestTemplateSid: string | null;

  // ── Incoming call agents per caller ID ──────────────────────────
  /** Map of cleanCallerId → array of agent emails assigned to handle incoming calls */
  incomingCallAgents: Record<string, string[]>;

  // ── Agent availability statuses (admin reads all agents) ──────
  /** Map of agent email → availability info, loaded from shared Firestore doc */
  agentStatuses: Record<string, { available: boolean; lastSeen: number; email: string; name: string }>;
  agentStatusesLoading: boolean;

  // ── TwiML app ───────────────────────────────────────────────────
  twiMLAppId: string | null;
  accountSid: string | null;
  /** The webhook URL used for TwiML app — also used as statusCallback */
  voiceWebhookUrl: string | null;

  // ── Hold music ──────────────────────────────────────────────────
  /** Admin-configured hold music URL (empty = Twilio default hold music) */
  holdMusicUrl: string | null;
  /** Whether holdMusicUrl has been loaded from Firestore */
  holdMusicLoaded: boolean;
  /** Load (and cache) the configured hold music URL */
  _getHoldMusicUrl(): Promise<string>;
  /** Update the cached hold music URL (called after saving in settings) */
  setHoldMusicUrl(url: string): void;

  // ── Auto call recording (KAN-180) ───────────────────────────────
  /** Last-read auto-record setting (informational; calls always re-read). null = not read yet */
  autoRecordCalls: boolean | null;
  /** Read the auto-record setting from Firestore — fresh on every call, so admin changes apply at once */
  _getAutoRecordSetting(): Promise<boolean>;
  /** Update the last-read auto-record setting (called after saving in settings) */
  setAutoRecordCalls(on: boolean): void;

  // ── Call Manager UI ─────────────────────────────────────────────
  callManagerOpen: boolean;
  callManagerTab: 'dialer' | 'history' | 'agents' | 'settings';
  crmCallManagerOpen: boolean;

  // ── Dismissed call widget IDs (hidden but still active) ────────
  dismissedCallIds: Set<string>;

  // ── Dialer ──────────────────────────────────────────────────────
  dialerNumber: string;

  // ── Context menu (Chrome extension right-click → call) ─────────
  /** Phone number passed via ?callNumber= when opened from Chrome extension context menu */
  contextMenuCallNumber: string;

  // ── Call history ────────────────────────────────────────────────
  callHistory: CallHistoryItem[];
  callHistoryLoading: boolean;
  callHistoryHasMore: boolean;
  /** Record of call SIDs → recordings. Keyed by both direct call_sid and parent call SID. */
  callsWithRecordings: Record<string, true>;
  /** Cached recordings keyed by parent call SID (for expanded detail view) */
  cachedRecordings: Record<string, CallRecording[]>;
  /** Raw account-level recordings from last fetch (reused by voicemail inbox) */
  accountRecordings: TwilioRecordingResource[];
  nextHistoryPage: string | null;
  /** Every raw Twilio call record fetched so far (all pages). Kept so loading
   *  more rebuilds the whole history: a transfer leg and its call can land on
   *  different pages, and matching within one page never connects them. */
  _historyRawCalls: TwilioCallResource[];

  // ── Internal refs (not serialized) ──────────────────────────────
  voiceHandler: TwilioVoiceHandler | null;
  api: TwilioAPI | null;
  durationTimers: Map<string, ReturnType<typeof setInterval>>;
  _rejectedCallIds: Set<string>;
  /** The caller's leg parked in a transfer room while the target is rung. The
   *  ring-back that follows a failed transfer is a NEW call with a new id, so
   *  this cannot live on the original call — without it, declining that
   *  ring-back left the caller connected to hold music with nobody coming. */
  _parkedCustomerLeg: string | null;
  _releaseParkedCustomer(): void;

  // ── Actions ─────────────────────────────────────────────────────
  initializeVoice(api: TwilioAPI, accountSid: string, extensionName: string, webhookBaseUrl: string, saasServiceName: string, isSharedUser?: boolean): Promise<void>;
  destroyVoice(): void;
  toggleAvailability(): Promise<void>;

  makeCall(recipientNumber: string): Promise<void>;
  acceptCall(callId: string): void;
  endCall(callId: string): void;
  ignoreCall(callId: string): void;
  muteCall(callId: string): void;
  holdCall(callId: string): Promise<void>;
  toggleRecording(callId: string): Promise<void>;
  sendDigits(callId: string, digits: string): void;
  /** Cold transfer — redirect the active call to a new target (number or agent email) */
  transferCall(callId: string, target: string): Promise<void>;
  /** Conference call — ring a number or agent email INTO the live call. The agent stays on. */
  addCallParticipant(callId: string, target: string): Promise<void>;
  /** Drop one added person from the conference call */
  removeCallParticipant(callId: string, participantCallSid: string): Promise<void>;

  setDialerNumber(number: string): void;
  setSelectedCallerId(value: string): void;
  setContextMenuCallNumber(number: string): void;

  toggleCallManager(): void;
  setCallManagerTab(tab: 'dialer' | 'history' | 'agents' | 'settings'): void;
  toggleCrmCallManager(): void;
  dismissCall(callId: string): void;
  undismissAllCalls(): void;

  /** Bootstrap API + accountSid without full voice/softphone init.
   *  Needed for call history to work even when softphone is offline. */
  ensureApiReady(extensionName: string): Promise<void>;

  fetchCallHistory(options?: {
    loadMore?: boolean;
    search?: string;
    direction?: 'inbound' | 'outbound';
    status?: 'completed' | 'no-answer' | 'failed' | 'busy' | 'canceled';
    dateFrom?: string; // YYYY-MM-DD
    dateTo?: string;   // YYYY-MM-DD
  }): Promise<void>;
  fetchCallRecordings(callId: string): Promise<CallRecording[]>;

  /** Subscribe to real-time call alerts from RTDB */
  subscribeToCallAlerts(extensionName: string): void;
  unsubscribeFromCallAlerts(): void;

  /** Custom call labels (admin only, saved in personal preferences) */
  customCallLabels: import('@/types/voice').CallLabel[];
  fetchCustomCallLabels(): Promise<void>;
  saveCustomCallLabel(label: import('@/types/voice').CallLabel): Promise<void>;
  deleteCustomCallLabel(value: string): Promise<void>;

  /** Call metadata — from UA_CALLS_INBOX/{callSid} (notesCount, labels, assignee) */
  callMetadata: Record<string, import('@/types/voice').CallMetadata>; // keyed by callSid
  fetchCallMetadataBatch(callSids: string[]): Promise<void>;
  updateCallMetadata(callSid: string, updates: { label?: string | null; assignee?: string | null; transferLegs?: Array<{ sid: string; agent: string }> }, customerNumber?: string): Promise<void>;

  /** Call notes — stored in UA_CALLS_INBOX/{callSid}/notes/{noteId} */
  callNotes: Record<string, import('@/types/voice').CallNote[]>; // keyed by callSid
  /** `extraSids` = the call's other legs; a transferred call stores each
   *  agent's notes under the leg they were on. */
  /** `ownLeg` marks a read of the agent's OWN live call leg (the widget during
   *  a call), where every note found is theirs by construction. Anywhere else
   *  the author filter always applies. */
  fetchCallNotes(callSid: string, extraSids?: string[], ownLeg?: boolean): Promise<void>;
  addCallNote(callSid: string, text: string, customerNumber?: string): Promise<void>;

  linkNumber(phoneNumberSid: string): Promise<void>;
  unlinkNumber(phoneNumberSid: string): Promise<void>;

  /** Link a WhatsApp sender to the TwiML app for voice calling */
  linkWhatsAppSender(senderSid: string): Promise<void>;
  /** Unlink a WhatsApp sender from voice calling */
  unlinkWhatsAppSender(senderSid: string): Promise<void>;
  /** Send a VOICE_CALL_REQUEST permission template to a contact */
  requestWhatsAppCallPermission(contactNumber: string): Promise<void>;
  /** Update permission state when contact responds to call request */
  handleWhatsAppCallPermissionResponse(contactNumber: string, accepted: boolean): void;

  /** Dismiss a missed call notification */
  dismissMissedCall(callId: string): void;
  /** Dismiss all missed call notifications */
  dismissAllMissedCalls(): void;

  /** Load assigned agents for all linked caller IDs from Firestore */
  loadIncomingCallAgents(): Promise<void>;
  /** Save assigned agents for a specific caller ID to Firestore (admin only) */
  saveIncomingCallAgents(callerId: string, agents: string[]): Promise<void>;

  /** Load all agents' availability statuses from shared Firestore doc (admin) */
  loadAgentStatuses(): Promise<void>;

  /** Fresh-fetch TwiML apps + phone numbers. Called when call manager opens. */
  refreshVoiceConfig(): Promise<void>;

  // Ring Groups
  ringGroups: RingGroup[];
  ringGroupsLoading: boolean;
  fetchRingGroups(): Promise<void>;
  /** Resolves false when the Firestore write failed — callers must keep the editor open. */
  saveRingGroup(group: RingGroup): Promise<boolean>;
  deleteRingGroup(id: string): Promise<void>;

  // Internal
  _handleVoiceEvent(event: VoiceEvent): void;
  _updateCallStatus(callId: string, status: CallStatus, extra?: Partial<ActiveCall>): void;
  /** Hang up a customer leg left waiting in a never-started hold conference */
  _releaseHeldCustomerLeg(callId: string): void;
  _startDurationTimer(callId: string): void;
  _stopDurationTimer(callId: string): void;
  _discoverOrCreateTwiMLApp(api: TwilioAPI, accountSid: string, webhookBaseUrl: string, saasServiceName: string, isSharedUser?: boolean): Promise<string | null>;
  _registerDevice(token: string, extensionName: string): Promise<void>;
}

/** Whether a note belongs to the viewer.
 *
 *  The author is stamped server-side, so this deliberately fails open: a note
 *  with no author counts as the viewer's rather than vanishing. The badge and
 *  the notes panel both go through here — if they judged this differently the
 *  count would disagree with the list it counts.
 */
/** Whose notes the viewer is looking at, lowercased.
 *
 *  Mirrors `getEffectiveUid()`: while an admin impersonates someone, the data
 *  read belongs to that person, so the identity matched against note authors
 *  and `client:<email>` call legs has to be theirs too — otherwise every row
 *  resolves to no leg and every count reads zero.
 */
function _viewerEmail(): string {
  const auth = useAuthStore.getState();
  return (auth.impersonatedUserEmail || auth.user?.email || '').toLowerCase();
}

function _isMyNote(note: { authorEmail?: string }, me: string): boolean {
  if (!me) return true;
  const author = (note.authorEmail || '').toLowerCase();
  return !author || author === me;
}

// ── Helper: Notify server about agent availability ──────────────

/** Returns true when the Cloud Function accepted the change, false otherwise. */
function _notifyAgentAvailability(extensionName: string, available: boolean): Promise<boolean> {
  const appStore = useAppStore.getState();
  const authState = useAuthStore.getState();
  const tpaService = extensionName.includes('for') ? extensionName.split('for')[0] : extensionName;

  console.log('[CallStore] Agent availability set to:', available);

  return callFunction('new_callServiceAuthorizedAPI', {
    appCode: extensionName,
    orgId: appStore.appConfig?.orgId,
    TPAService: tpaService,
    action: 'SET_CALL_AGENT_AVAILABILITY',
    data: { available },
    _ua_lic_adminUserID: authState.selectedAdminUserId || undefined,
    headers: { 'Authorization': 'Bearer {{AUTHTOKEN}}' },
  }).then((res) => {
    // callFunction already unwraps HttpsCallableResult — res IS the CF's
    // return object: {'status':'success'|'error', userResponse: boolean}.
    const envelope = res as Record<string, unknown> | null | undefined;
    const code = (envelope?.code ?? envelope?.status) as number | string | undefined;
    // Failure is signalled by a STRING status ('error'), not a numeric code.
    const rejected =
      Boolean(envelope?.error) ||
      envelope?.status === 'error' ||
      envelope?.userResponse === false ||
      (typeof code === 'number' && code >= 400);
    if (rejected) {
      console.warn('[CallStore] Server rejected agent availability:', envelope?.error ?? code);
      return false;
    }
    console.log('[CallStore] Agent availability confirmed by server:', available);
    return true;
  }).catch((err) => {
    console.warn('[CallStore] Failed to set agent availability:', err);
    return false;
  });
}

// ── Helper: Get Firestore personal settings doc path ────────────

function _getPersonalSettingsDocPath(): string | null {
  const uid = getEffectiveUid();
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;

  if (!uid || !extensionName || !orgId || !saasCode) return null;

  return `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}/personal/${uid}`;
}

// ── Helper: Get Firestore shared settings doc path ──────────────

function _getSharedDocPath(): string | null {
  const uid = getEffectiveUid();
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;

  if (!uid || !extensionName || !orgId || !saasCode) return null;

  return `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}/shared/${uid}`;
}

// Agent status writes are handled entirely by the CF (SET_CALL_AGENT_AVAILABILITY).

// ── Helper: Cache TwiML App SID in localStorage ─────────────────

function _getTwiMLAppCacheKey(): string | null {
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const uid = getEffectiveUid();
  if (!extensionName || !orgId || !uid) return null;
  return `ua_voice_twiml_app_${extensionName}_${orgId}_${uid}`;
}

function _getCachedTwiMLAppId(): string | null {
  const key = _getTwiMLAppCacheKey();
  if (!key) return null;
  try { return localStorage.getItem(key); } catch { return null; }
}

function _setCachedTwiMLAppId(sid: string) {
  const key = _getTwiMLAppCacheKey();
  if (!key) return;
  try { localStorage.setItem(key, sid); } catch { /* */ }
}

// ── Helper: Cache Voice Token in localStorage ───────────────────

const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000; // 5-min buffer before actual exp

/** Decode JWT payload and extract `exp` (seconds since epoch) */
function _getTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
}

function _getVoiceTokenCacheKey(): string | null {
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const uid = getEffectiveUid();
  if (!extensionName || !orgId || !uid) return null;
  return `ua_voice_token_${extensionName}_${orgId}_${uid}`;
}

function _getCachedVoiceToken(): string | null {
  const key = _getVoiceTokenCacheKey();
  if (!key) return null;
  try {
    const token = localStorage.getItem(key);
    if (!token) return null;
    const expiry = _getTokenExpiry(token);
    if (!expiry || Date.now() > expiry - TOKEN_EXPIRY_BUFFER_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

function _setCachedVoiceToken(token: string) {
  const key = _getVoiceTokenCacheKey();
  if (!key) return;
  try { localStorage.setItem(key, token); } catch { /* */ }
}

// ── Helper: Persist availability in localStorage ────────────────

function _getAvailabilityCacheKey(): string | null {
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  // Availability is a per-AGENT choice — key it on the signed-in user's uid.
  // getEffectiveUid() resolves to the ADMIN's uid for shared users, which would
  // make every agent of the same admin share (and overwrite) one cached value.
  const uid = useAuthStore.getState().user?.uid;
  if (!extensionName || !orgId || !uid) return null;
  return `ua_voice_available_${extensionName}_${orgId}_${uid}`;
}

function _getCachedAvailability(): boolean {
  const key = _getAvailabilityCacheKey();
  if (!key) return true; // default available if no key yet
  try {
    const val = localStorage.getItem(key);
    return val === null ? true : val === '1';
  } catch {
    return true;
  }
}

function _setCachedAvailability(available: boolean) {
  const key = _getAvailabilityCacheKey();
  if (!key) return;
  try { localStorage.setItem(key, available ? '1' : '0'); } catch { /* */ }
}

// ── Helper: Build combined caller IDs from PSTN + WhatsApp numbers ──

function _buildCallerIds(
  pstnNumbers: VoiceNumber[],
  waNumbers: VoiceNumber[]
): CallerID[] {
  // All voice-capable numbers are available as caller IDs for outgoing calls.
  // "linked" only controls whether incoming calls are handled by our TwiML app.
  const pstnCallerIds: CallerID[] = pstnNumbers
    .map((n) => ({
      id: n.sid,
      label: n.friendlyName,
      value: n.phoneNumber,
      configureUrl: `https://console.twilio.com/us1/develop/phone-numbers/manage/incoming/${n.sid}/configure`,
      channel: 'SMS' as const,
    }));

  const waCallerIds: CallerID[] = waNumbers
    .filter((n) => n.linked)
    .map((n) => ({
      id: n.sid,
      label: `${n.friendlyName} (WhatsApp)`,
      value: n.phoneNumber,
      channel: 'WHATSAPP' as const,
    }));

  return [...pstnCallerIds, ...waCallerIds];
}

/**
 * True when a LIVE call's floating widget was dismissed — drives the pulsing
 * "reopen" affordance on the nav/header phone buttons. Shared so the two
 * buttons can't drift apart.
 */
export function selectHasDismissedActiveCalls(
  s: Pick<CallStore, 'activeCalls' | 'dismissedCallIds'>
): boolean {
  return s.activeCalls.some(
    (c) => s.dismissedCallIds.has(c.id) && isLiveCallStatus(c.status)
  );
}

export const useCallStore = create<CallStore>((set, get) => ({
  // ── Initial state ───────────────────────────────────────────────
  softphoneState: 'unregistered',
  isAvailable: false,
  voiceInitWaiting: false,
  availabilityUpdating: false,
  voiceError: null,
  activeCalls: [],
  missedCalls: [],
  callerIds: [],
  selectedCallerId: null,
  allVoiceNumbers: [],
  linkingNumberSid: null,
  whatsAppVoiceNumbers: [],
  linkingWhatsAppSenderSid: null,
  whatsAppCallPermissions: {},
  _cachedCallRequestTemplateSid: null,
  incomingCallAgents: {},
  agentStatuses: {},
  agentStatusesLoading: false,
  twiMLAppId: null,
  accountSid: null,
  voiceWebhookUrl: null,
  holdMusicUrl: null,
  holdMusicLoaded: false,
  autoRecordCalls: null,
  callManagerOpen: false,
  callManagerTab: 'dialer',
  crmCallManagerOpen: false,
  dismissedCallIds: new Set(),
  dialerNumber: '',
  contextMenuCallNumber: '',
  callHistory: [],
  callHistoryLoading: false,
  callHistoryHasMore: false,
  callsWithRecordings: {},
  cachedRecordings: {},
  accountRecordings: [],
  customCallLabels: [],
  callMetadata: {},
  callNotes: {},
  nextHistoryPage: null,
  _historyRawCalls: [],
  ringGroups: [],
  ringGroupsLoading: false,
  voiceHandler: null,
  api: null,
  durationTimers: new Map(),
  _rejectedCallIds: new Set(),
  _parkedCustomerLeg: null,

  // ── Bootstrap API (without full voice init) ────────────────────

  ensureApiReady: async (extensionName) => {
    const { api, accountSid } = get();
    if (api && accountSid) {
      // API already ready — still populate twiMLAppId from cache if missing
      if (!get().twiMLAppId) {
        const cached = _getCachedTwiMLAppId();
        if (cached) set({ twiMLAppId: cached });
      }
      return;
    }

    const newApi = new TwilioAPI(extensionName);
    try {
      const resp = await newApi.getAccounts();
      const sid = resp.data?.accounts?.[0]?.sid;
      if (sid) {
        const cachedTwiML = _getCachedTwiMLAppId();
        set({ api: newApi, accountSid: sid, ...(cachedTwiML && !get().twiMLAppId ? { twiMLAppId: cachedTwiML } : {}) });
      }
    } catch (err) {
      console.warn('[CallStore] ensureApiReady failed:', err);
    }
  },

  // ── Initialize Voice ────────────────────────────────────────────

  initializeVoice: async (api, accountSid, extensionName, webhookBaseUrl, saasServiceName, isSharedUser) => {
    const { voiceHandler: existingHandler } = get();
    if (existingHandler) {
      existingHandler.destroy();
    }

    // Restore availability from localStorage before init
    const cachedAvailable = _getCachedAvailability();
    set({ api, accountSid, softphoneState: 'registering', voiceInitWaiting: false, voiceError: null, voiceWebhookUrl: webhookBaseUrl, isAvailable: cachedAvailable });

    try {
      // ── Step 1: Resolve TwiML App (always discover to ensure URLs are up to date) ──
      console.log('[CallStore] Step 1: Discovering/updating TwiML app... webhookBaseUrl:', webhookBaseUrl);
      let twiMLAppId: string | null = await get()._discoverOrCreateTwiMLApp(api, accountSid, webhookBaseUrl, saasServiceName, isSharedUser);

      if (!twiMLAppId) {
        // _discoverOrCreateTwiMLApp already set the error state
        return;
      }

      set({ twiMLAppId });
      _setCachedTwiMLAppId(twiMLAppId);

      // ── Step 2: Skip phone number discovery on page load ──
      // Numbers are fetched fresh when the Call Manager opens (refreshVoiceConfig)
      console.log('[CallStore] Step 2: Skipped phone number discovery (deferred to Call Manager open)');

      // ── Step 3: Get voice access token (use cache if valid) ──
      let voiceToken: string | null = _getCachedVoiceToken();
      const usedCachedToken = !!voiceToken;

      if (voiceToken) {
        console.log('[CallStore] Step 3: Using cached voice token');
      } else {
        console.log('[CallStore] Step 3: No cached token, fetching fresh for TwiML app:', twiMLAppId);
        const tokenResp = await api.getVoiceToken(twiMLAppId);

        if (!tokenResp.data?.token) {
          console.error('[CallStore] Step 3 FAILED: No token in response. Error:', tokenResp.error);
          // If cached TwiML app is stale, clear cache and retry full discovery
          if (_getCachedTwiMLAppId()) {
            console.log('[CallStore] Clearing stale TwiML app cache and retrying...');
            const key = _getTwiMLAppCacheKey();
            if (key) try { localStorage.removeItem(key); } catch { /* */ }
            twiMLAppId = await get()._discoverOrCreateTwiMLApp(api, accountSid, webhookBaseUrl, saasServiceName, isSharedUser);
            if (!twiMLAppId) return;
            set({ twiMLAppId });
            _setCachedTwiMLAppId(twiMLAppId);
            const retryResp = await api.getVoiceToken(twiMLAppId);
            if (!retryResp.data?.token) {
              console.error('[CallStore] Step 3 retry FAILED');
              set({ softphoneState: 'error' });
              return;
            }
            voiceToken = retryResp.data.token;
          } else {
            set({ softphoneState: 'error' });
            return;
          }
        } else {
          voiceToken = tokenResp.data.token;
        }

        // Cache the fresh token
        if (voiceToken) _setCachedVoiceToken(voiceToken);
      }

      // ── Step 4: Initialize Twilio Device ──
      await get()._registerDevice(voiceToken!, extensionName);
      if(get().softphoneState === 'error' && usedCachedToken) {
        const tokenKey = _getVoiceTokenCacheKey();
        if(tokenKey) {
          try{
            localStorage.removeItem(tokenKey);
          }
          catch {

          }
        }

        const freshResp = await api.getVoiceToken(twiMLAppId);
        if(freshResp.data?.token) {
          _setCachedVoiceToken(freshResp.data.token);
          await get()._registerDevice(freshResp.data.token, extensionName);
        }
      }
    } catch (error) {
      console.error('[CallStore] Voice initialization failed at unexpected point:', error);
      set({ softphoneState: 'error' });
    }
  },

  // ── Destroy Voice ───────────────────────────────────────────────

  destroyVoice: () => {
    const { voiceHandler, durationTimers } = get();
    if (voiceHandler) {
      voiceHandler.destroy();
    }
    // Clear all timers
    durationTimers.forEach((timer) => clearInterval(timer));
    set({
      voiceHandler: null,
      softphoneState: 'unregistered',
      voiceError: null,
      activeCalls: [],
      missedCalls: [],
      durationTimers: new Map(),
    });
  },

  // ── Toggle Availability ─────────────────────────────────────────

  toggleAvailability: async () => {
    const { isAvailable, agentStatuses, availabilityUpdating } = get();
    // Re-entrancy guard — ignore clicks while a server update is in flight,
    // regardless of which component fired the toggle.
    if (availabilityUpdating) return;
    const extensionName = useAppStore.getState().extensionName;
    const newAvailable = !isAvailable;
    const userEmail = useAuthStore.getState().user?.email;

    _setCachedAvailability(newAvailable);

    // Update local agentStatuses immediately so the UI count refreshes
    if (userEmail) {
      set({
        agentStatuses: {
          ...agentStatuses,
          [userEmail]: {
            ...(agentStatuses[userEmail] || { email: userEmail, name: userEmail }),
            available: newAvailable,
            lastSeen: Date.now(),
          },
        },
      });
    }

    // Notify CF; availabilityUpdating (NOT voiceInitWaiting — that flag means
    // "voice is initializing" and drives the nav spinner) marks the round trip.
    if (extensionName) {
      set({ isAvailable: newAvailable, availabilityUpdating: true });
      const accepted = await _notifyAgentAvailability(extensionName, newAvailable);
      if (!accepted) {
        // Server never applied the change — roll back so the toggle can't
        // show green while calls aren't actually routed to this agent.
        _setCachedAvailability(isAvailable);
        if (userEmail) {
          const statuses = get().agentStatuses;
          set({
            agentStatuses: {
              ...statuses,
              [userEmail]: {
                ...(statuses[userEmail] || { email: userEmail, name: userEmail }),
                available: isAvailable,
                lastSeen: Date.now(),
              },
            },
          });
        }
        set({ isAvailable, availabilityUpdating: false });
        toast.error('Could not update availability — please try again');
        return;
      }
      set({ availabilityUpdating: false });
    } else {
      set({ isAvailable: newAvailable });
    }
  },

  // ── Call Operations ─────────────────────────────────────────────

  makeCall: async (rawRecipientNumber) => {
    const { voiceHandler, selectedCallerId, activeCalls, callerIds } = get();
    if (!voiceHandler || !selectedCallerId) {
      throw new Error('Voice not ready or no caller ID selected');
    }

    // Detect WhatsApp call based on the selected caller ID's channel
    const selectedCallerObj = callerIds.find((c) => c.value === selectedCallerId);
    const isWhatsAppCall = selectedCallerObj?.channel === 'WHATSAPP';

    // Twilio assumes any number without a '+' is US and adds +1. We only add the
    // '+' (never a country code), so "6595422149" goes out as "+6595422149" exactly
    // as typed. Numbers that already start with '+' are sent through unchanged.
    const recipientNumber = toDialE164(rawRecipientNumber);

    const uiCallId = `ui_${Date.now()}`;
    // Read the admin user ID from auth store (needed by TwiML webhook for routing)
    const authState = useAuthStore.getState();
    const adminUserId = authState.selectedAdminUserId || authState.user?.uid || '';
    console.log('[CallStore] makeCall:', recipientNumber, '→', selectedCallerId, 'admin:', adminUserId, 'whatsapp:', isWhatsAppCall);

    // Close Call Manager modal when starting a call (floating widget takes over)
    set({ callManagerOpen: false });

    // Add to active calls immediately
    const newCall: ActiveCall = {
      id: uiCallId,
      tpaCallId: null,
      direction: 'outbound',
      status: 'connecting',
      customerNumber: recipientNumber,
      companyCallerId: selectedCallerId,
      startTime: null,
      duration: 0,
      isMuted: false,
      isRecording: false,
      isOnHold: false,
      callChannel: isWhatsAppCall ? 'whatsapp' : 'pstn',
    };

    // Auto-dismiss any missed call notifications from this number
    const cleanRecipient = recipientNumber.replace(/[^0-9+]/g, '');
    const remainingMissed = get().missedCalls.filter(
      (c) => c.customerNumber.replace(/[^0-9+]/g, '') !== cleanRecipient
    );

    set({ activeCalls: [...activeCalls, newCall], missedCalls: remainingMissed });

    try {
      await voiceHandler.makeCall(selectedCallerId, recipientNumber, uiCallId, adminUserId, isWhatsAppCall);
    } catch (error) {
      console.error('[CallStore] makeCall failed:', error);
      get()._updateCallStatus(uiCallId, 'error', {
        errorMessage: error instanceof Error ? error.message : 'Call failed',
      });
    }
  },

  acceptCall: (callId) => {
    // The agent picked the caller back up; they are no longer parked.
    set({ _parkedCustomerLeg: null });
    get().voiceHandler?.acceptCall(callId);
    // Close Call Manager modal (floating widget takes over)
    set({ callManagerOpen: false });
  },

  /** End the caller parked in a transfer room, if there is one.
   *
   *  A failed transfer rings the agent back as a NEW call. Declining or
   *  ending that call used to leave the caller in the room listening to hold
   *  music forever, because nothing tied the two together. */
  _releaseParkedCustomer: () => {
    const parked = get()._parkedCustomerLeg;
    if (!parked) return;
    const { api, accountSid } = get();
    set({ _parkedCustomerLeg: null });
    if (api && accountSid) {
      console.log('[CallStore] Releasing parked caller leg', parked);
      api.updateCall(accountSid, parked, 'Status=completed').catch((err) => {
        console.error('[CallStore] Failed to release parked caller:', err);
      });
    }
  },

  endCall: (callId) => {
    const call = get().activeCalls.find((c) => c.id === callId);
    get()._releaseHeldCustomerLeg(callId);
    get()._releaseParkedCustomer();
    const hungUp = get().voiceHandler?.endCall(callId) ?? false;
    if (!hungUp) {
      // The browser has no usable connection for this call, so nothing reached
      // Twilio — but the UI is about to call it ended. That is how declining a
      // transfer ring-back closed the popup while the line stayed up. Hang the
      // leg up server-side instead of leaving a live call nobody can see.
      const { api, accountSid } = get();
      const legSid = call?.tpaCallId || callId;
      if (api && accountSid && legSid) {
        console.warn('[CallStore] endCall: no browser connection, hanging up', legSid, 'server-side');
        api.updateCall(accountSid, legSid, 'Status=completed').catch((err) => {
          console.error('[CallStore] Server-side hangup failed:', err);
        });
      }
    }
    get()._updateCallStatus(callId, 'ended');
    get()._stopDurationTimer(callId);
    // Track rejected inbound calls so re-attempts are auto-rejected
    if (call?.direction === 'inbound') {
      get()._rejectedCallIds.add(callId);
    }
  },

  /**
   * A customer waiting in a never-started hold conference isn't bridged to
   * the agent leg anymore — hang their leg up explicitly or they'd keep
   * hearing hold music forever. (Once the conference has started,
   * endConferenceOnExit on the agent participant tears it down for us.)
   * Keyed on customerCallSid alone: it's saved before the hold dance starts,
   * so a half-failed hold still gets cleaned up; never-held calls no-op.
   * Clears the SID first so the endCall + SDK-disconnect double path only
   * fires one hangup request.
   */
  _releaseHeldCustomerLeg: (callId) => {
    const call = get().activeCalls.find((c) => c.id === callId);
    if (!call?.customerCallSid || call.holdConferenceStarted) return;
    const { api, accountSid } = get();
    const customerSid = call.customerCallSid;
    set({
      activeCalls: get().activeCalls.map((c) =>
        c.id === callId ? { ...c, customerCallSid: null } : c
      ),
    });
    if (api && accountSid) {
      api.updateCall(accountSid, customerSid, 'Status=completed').catch((err) => {
        console.error('[CallStore] Failed to end held customer leg:', err);
      });
    }
  },

  ignoreCall: (callId) => {
    get()._releaseParkedCustomer();
    get().voiceHandler?.ignoreCall(callId);
    get()._updateCallStatus(callId, 'ignored');
    // Track ignored inbound calls so re-attempts are auto-rejected
    const call = get().activeCalls.find((c) => c.id === callId);
    if (call?.direction === 'inbound') {
      get()._rejectedCallIds.add(callId);
    }
  },

  muteCall: (callId) => {
    const { voiceHandler, activeCalls } = get();
    const call = activeCalls.find((c) => c.id === callId);
    if (call && voiceHandler) {
      voiceHandler.muteCall(callId, !call.isMuted);
    }
  },

  /**
   * Hold / resume — actually places the remote party on hold via the Twilio
   * REST API so they hear the configured hold music (KAN-76).
   *
   * The call starts life as a plain <Dial> bridge (agent client leg ↔ customer
   * leg), which has no hold concept, so the first hold moves both legs into a
   * named conference room:
   *   1. The customer leg is redirected to wait in the room
   *      (startConferenceOnEnter=false) with the hold music as waitUrl.
   *   2. The agent leg is parked on a long <Pause> so the client connection
   *      stays alive. Redirect order is child-leg-first — a <Dial>-created
   *      child is torn down if its parent leaves the bridge first.
   * Resume redirects the agent leg into the same room with
   * startConferenceOnEnter=true, which starts the conference and connects the
   * waiting customer. From then on, hold/resume is a Participant API update
   * (Hold=true/false with HoldUrl), since both legs live in the conference.
   */
  holdCall: async (callId) => {
    const { api, accountSid, activeCalls } = get();
    const call = activeCalls.find((c) => c.id === callId);
    if (!call || call.holdPending) return;
    if (!api || !accountSid) {
      console.warn('[CallStore] holdCall: API not ready');
      toast.error('Calling service is not ready yet — try again in a moment');
      return;
    }

    const agentSid = call.tpaCallId || callId;

    // The hold dance spans several network round-trips. If the call reaches a
    // terminal state midway (remote hangup, agent pressed End), stop touching
    // legs that are being torn down.
    const callIsLive = () => {
      const fresh = get().activeCalls.find((c) => c.id === callId);
      return !!fresh && !TERMINAL_CALL_STATUSES.includes(fresh.status);
    };
    // Twilio rejects XML with unescaped attribute chars; music URLs are user input
    const escapeXmlAttr = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');

    // Redirecting a leg to the same TwiML is idempotent, and the proxy CF
    // occasionally reports an error for a request Twilio actually applied —
    // retry once before treating it as a real failure.
    const updateCallWithRetry = async (sid: string, body: string, what: string) => {
      let resp = await api.updateCall(accountSid, sid, body);
      if (resp.error) {
        console.warn(`[CallStore] ${what} errored, retrying once:`, resp.error);
        resp = await api.updateCall(accountSid, sid, body);
      }
      if (resp.error) {
        throw new Error(typeof resp.error === 'string' ? resp.error : `${what} failed`);
      }
    };

    get()._updateCallStatus(callId, call.status, { holdPending: true });
    // Path of a hold-intent flag written this invocation — deleted on failure
    // so a leaked flag can't turn a genuine hangup into a 4-hour <Pause>.
    let writtenHoldIntentPath: string | null = null;
    try {
      const musicUrl = await get()._getHoldMusicUrl();
      if (!callIsLive()) {
        get()._updateCallStatus(callId, call.status, { holdPending: false });
        return;
      }
      // Twilio plays a bare audio waitUrl/HoldUrl ONCE — a 5s clip leaves the
      // held caller in silence after 5 seconds. Wrap the file in
      // <Play loop="0"> TwiML (served by Twilio's own echo twimlet) so the
      // clip repeats for the whole hold. Empty = Twilio's default music,
      // which loops by itself.
      const musicTwimlUrl = musicUrl
        ? `https://twimlets.com/echo?Twiml=${encodeURIComponent(`<Response><Play loop="0">${escapeXmlAttr(musicUrl)}</Play></Response>`)}`
        : '';

      // ── First hold: move both legs out of the <Dial> bridge ────────
      if (!call.holdRoom) {
        // Resolve the customer leg: it's either the parent of the agent's
        // client leg (inbound) or its child (outbound).
        let customerSid: string | null = null;
        let customerIsChild = false;
        const callResp = await api.getCallById(accountSid, agentSid);
        if (callResp.data?.parent_call_sid) {
          customerSid = callResp.data.parent_call_sid;
        } else {
          const childResp = await api.getChildCalls(accountSid, agentSid);
          // Only a live child can be the current bridge partner — a completed
          // SID from an earlier dial on this call must not be redirected.
          customerSid =
            childResp.data?.calls?.find((c) => c.status === 'in-progress' || c.status === 'ringing')
              ?.sid ?? null;
          customerIsChild = true;
        }
        if (!customerSid) {
          throw new Error('Could not resolve the remote call leg');
        }
        if (!callIsLive()) {
          get()._updateCallStatus(callId, call.status, { holdPending: false });
          return;
        }
        // Persist the customer leg before touching anything — if the dance
        // fails halfway the customer may already be parked on hold music, and
        // endCall needs this SID to hang their leg up (no zombie music).
        get()._updateCallStatus(callId, call.status, { customerCallSid: customerSid });

        // Flag the parent leg for the backend's <Dial action> callback BEFORE
        // redirecting: the first redirect ends that leg's <Dial>, and without
        // this flag the backend answers the callback with an implicit hangup
        // (killing the leg mid-hold). With it, the backend responds <Pause>.
        // Written first and awaited — if it fails, abort while the call is
        // still fully intact.
        const parentSid = customerIsChild ? agentSid : customerSid;
        const uid = getEffectiveUid();
        const extensionName = useAppStore.getState().extensionName;
        if (!uid || !extensionName) throw new Error('Missing user context for hold');
        const holdIntentPath = `ulgebraUsers/${uid}/extensions/${extensionName}/voiceHoldIntents/${parentSid}`;
        await setDoc(doc(db, holdIntentPath), { at: Date.now() });
        writtenHoldIntentPath = holdIntentPath;

        const room = `hold_${agentSid}`;
        const waitAttrs = musicTwimlUrl
          ? ` waitUrl="${escapeXmlAttr(musicTwimlUrl)}" waitMethod="GET"`
          : ''; // no waitUrl → Twilio's default hold music
        const customerTwiml = `<Response><Dial><Conference beep="false" startConferenceOnEnter="false" endConferenceOnExit="false"${waitAttrs}>${room}</Conference></Dial></Response>`;
        // Park the agent leg so the browser connection survives the hold
        const agentTwiml = '<Response><Pause length="14400"/></Response>';

        const redirects: Array<[string, string, string]> = customerIsChild
          ? [[customerSid, customerTwiml, 'Customer hold redirect'], [agentSid, agentTwiml, 'Agent park redirect']]
          : [[agentSid, agentTwiml, 'Agent park redirect'], [customerSid, customerTwiml, 'Customer hold redirect']];
        for (const [sid, twiml, what] of redirects) {
          if (!callIsLive()) {
            // Call died mid-dance — free anything already parked and stop.
            get()._releaseHeldCustomerLeg(callId);
            get()._updateCallStatus(callId, call.status, { holdPending: false });
            return;
          }
          await updateCallWithRetry(sid, `Twiml=${encodeURIComponent(twiml)}`, what);
        }

        get()._updateCallStatus(callId, 'on-hold', {
          isOnHold: true,
          holdPending: false,
          holdRoom: room,
          customerCallSid: customerSid,
          holdConferenceStarted: false,
        });
        return;
      }

      // ── First resume: start the conference by joining the agent leg ─
      if (call.isOnHold && !call.holdConferenceStarted) {
        const agentTwiml = `<Response><Dial><Conference beep="false" startConferenceOnEnter="true" endConferenceOnExit="true">${call.holdRoom}</Conference></Dial></Response>`;
        await updateCallWithRetry(agentSid, `Twiml=${encodeURIComponent(agentTwiml)}`, 'Resume redirect');
        get()._updateCallStatus(callId, 'connected', {
          isOnHold: false,
          holdPending: false,
          holdConferenceStarted: true,
        });
        return;
      }

      // ── Later holds/resumes: toggle the customer participant ───────
      let conferenceSid = call.holdConferenceSid ?? null;
      if (!conferenceSid) {
        // The conference is created when the agent joins; allow a moment for
        // it to show up. FriendlyName filter keeps the lookup exact even with
        // many concurrent conferences on the account (the list is paginated).
        for (let attempt = 0; attempt < 3 && !conferenceSid; attempt++) {
          const confResp = await api.getConferences(accountSid, 'in-progress', call.holdRoom);
          conferenceSid = confResp.data?.conferences?.find((c) => c.friendly_name === call.holdRoom)?.sid ?? null;
          if (!conferenceSid) await new Promise((r) => setTimeout(r, 700));
        }
        if (!conferenceSid) throw new Error('Hold conference not found');
      }
      if (!callIsLive()) {
        get()._updateCallStatus(callId, call.status, { holdPending: false });
        return;
      }

      const goingOnHold = !call.isOnHold;
      const params = goingOnHold
        ? `Hold=true${musicTwimlUrl ? `&HoldUrl=${encodeURIComponent(musicTwimlUrl)}&HoldMethod=GET` : ''}`
        : 'Hold=false';
      // Same retry rationale as the redirects: the proxy CF occasionally
      // errors on a request Twilio applied, and re-setting Hold is idempotent.
      let resp = await api.updateConferenceParticipant(accountSid, conferenceSid, call.customerCallSid || '', params);
      if (resp.error) {
        console.warn('[CallStore] Participant hold update errored, retrying once:', resp.error);
        resp = await api.updateConferenceParticipant(accountSid, conferenceSid, call.customerCallSid || '', params);
      }
      if (resp.error) throw new Error(typeof resp.error === 'string' ? resp.error : 'Failed to update hold state');

      get()._updateCallStatus(callId, goingOnHold ? 'on-hold' : 'connected', {
        isOnHold: goingOnHold,
        holdPending: false,
        holdConferenceSid: conferenceSid,
      });
    } catch (err) {
      console.error('[CallStore] holdCall failed:', err);
      // An unconsumed flag would turn a genuine hangup in the next 2 minutes
      // into a long <Pause> on Twilio's meter. If the backend already
      // consumed it (the first redirect landed), this delete is a no-op.
      if (writtenHoldIntentPath) {
        deleteDoc(doc(db, writtenHoldIntentPath)).catch(() => {});
      }
      const detail = err instanceof Error && err.message ? ` — ${err.message}` : '';
      toast.error(`${call.isOnHold ? 'Failed to resume the call' : 'Failed to place the call on hold'}${detail}`);
      get()._updateCallStatus(callId, call.status, { holdPending: false });
    }
  },

  toggleRecording: async (callId) => {
    const { api, accountSid, voiceHandler, activeCalls } = get();
    if (!api || !accountSid) return;

    const call = activeCalls.find((c) => c.id === callId);
    if (!call) return;

    const doRecord = !call.isRecording;
    const isExisting = voiceHandler?.hasRecordingStarted(callId) ?? false;

    const data: Record<string, string> = isExisting
      ? doRecord
        ? { Status: 'in-progress' }
        : { Status: 'paused', PauseBehavior: 'skip' }
      : {
          RecordingStatusCallbackEvent: 'in-progress completed',
          RecordingChannels: 'dual',
        };

    const tpaCallId = call.tpaCallId || callId;
    const resp = await api.manageRecording(accountSid, tpaCallId, data, isExisting);

    if (!resp.error) {
      if (!isExisting) {
        voiceHandler?.markRecordingStarted(callId);
      }
      set({
        activeCalls: get().activeCalls.map((c) =>
          c.id === callId ? { ...c, isRecording: doRecord } : c
        ),
      });
    }
  },

  sendDigits: (callId, digits) => {
    get().voiceHandler?.sendDigits(callId, digits);
  },

  transferCall: async (callId, target) => {
    const { api, accountSid, activeCalls } = get();
    if (!api || !accountSid) return;

    const call = activeCalls.find((c) => c.id === callId);
    if (!call) return;

    const callSid = call.tpaCallId || callId;
    const isClient = target.includes('@');
    const transferTo = isClient ? `client:${target}` : toDialE164(target);

    // Caller ID for the new leg. Two values have to be filtered out first:
    //  - a WhatsApp sender id ('whatsapp:+1...') is not a valid PSTN callerId,
    //    so strip the prefix before handing it to Twilio;
    //  - on a leg we RECEIVED through a transfer, companyCallerId is this
    //    agent's own client identity. Legs created by the Conference
    //    Participants API execute no TwiML, so they carry no companyCallerID
    //    parameter and the voice handler falls back to `parameters.To`
    //    ('client:b@acme.com'). Twilio rejects that as `From`, which broke
    //    every second hop of a chain: A→B worked, B→C and B→any phone number
    //    failed. On such a leg the number we dialled the agent FROM arrives as
    //    `parameters.From`, i.e. in customerNumber — that is the one to use.
    const dialableCallerId = (value: string): string => {
      const v = (value || '').replace(/^whatsapp:/, '');
      return v && !/^client:/i.test(v) ? v : '';
    };
    const callerIdForTransfer =
      dialableCallerId(call.companyCallerId) || dialableCallerId(call.customerNumber);
    if (!callerIdForTransfer) {
      console.error('[CallStore] Transfer aborted — no dialable caller ID', {
        companyCallerId: call.companyCallerId,
        customerNumber: call.customerNumber,
      });
      toast.error('Transfer failed — no caller ID available for this call');
      return;
    }

    // proxyRequest NEVER throws — every api.* failure comes back as
    // { success: false }. Abort on the first failed step and leave the call
    // untouched (status stays 'connected', so the agent keeps all controls).
    const fail = (step: string, error?: string) => {
      console.error(`[CallStore] Transfer failed (${step}):`, error);
      toast.error(`Transfer failed — you are still connected to the caller`);
    };

    try {
      // Validate agent-email targets BEFORE touching the call — a typo'd
      // client identity produces perfectly valid TwiML, so nothing can fail
      // afterwards; the redirected customer leg just dies while the UI would
      // claim "Call Forwarded".
      if (isClient) {
        const selfEmail = useAuthStore.getState().user?.email;
        if (selfEmail && selfEmail.toLowerCase() === target.trim().toLowerCase()) {
          toast.error('Transfer failed — you cannot transfer a call to yourself');
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target.trim())) {
          toast.error(`Transfer failed — "${target}" is not a valid agent email`);
          return;
        }

        // Anything beyond "is it me" and "is it an address" is ADVISORY only.
        //
        // This used to hard-reject a target that wasn't in the known-agents
        // list, and that list is systematically incomplete:
        //  - orgUsers is loaded with limit(200) and no orderBy, so in a
        //    250-agent org the ~50 lexicographically-last emails are NEVER in
        //    it — those agents were untransferable, and adding new agents
        //    shifts the window so targets that worked yesterday fail today;
        //  - the admin is deliberately absent from orgUsers, and recognising
        //    them depends on auth state (invitedAdminList /
        //    selectedAdminUserEmail) that an agent added mid-session has not
        //    loaded — live 3-agent testing hit exactly this ("no agent found"
        //    for the org's own admin).
        //
        // The rejection existed because a failed transfer used to be fatal
        // (customer redirected straight at the target). It no longer is: the
        // customer is parked, the target must ANSWER before the transfer
        // completes, and the agent is rung back on failure. A wrong guess now
        // costs the ring timeout; a wrong REJECTION blocks a legitimate
        // transfer with no workaround.
        const usersStore = useAppUsersStore.getState();
        if (Object.keys(usersStore.orgUsers).length === 0) {
          await usersStore.loadOrgUsers().catch(() => { /* advisory best-effort */ });
        }
        const authState = useAuthStore.getState();
        const known = [
          ...Object.values(useAppUsersStore.getState().orgUsers).map((u) => u.email),
          ...Object.values(authState.invitedAdminList || {}).map((a) => a.invitedAdminUserEmail),
          authState.selectedAdminUserEmail,
        ]
          .filter((e): e is string => !!e)
          .map((e) => e.toLowerCase());
        if (known.length > 0 && !known.includes(target.trim().toLowerCase())) {
          console.warn(
            `[CallStore] Transfer target "${target}" is not in this agent's known list (list may be truncated/stale) — attempting anyway`
          );
        }
      }

      // Validate phone targets BEFORE touching the call. The redirect path
      // cannot fail afterwards: updateCall accepts any well-formed TwiML, and
      // a <Dial> to a junk number then kills the customer leg while the UI
      // would claim "Call Forwarded". Lookup's basic tier is free.
      let phoneTarget = transferTo;
      if (!isClient && api.lookupPhoneNumber) {
        // Bare national numbers (no '+') are resolved against the caller ID's
        // country so e.g. a local 10-digit number still validates.
        const countryCode = !transferTo.startsWith('+')
          ? parsePhoneNumberFromString(callerIdForTransfer)?.country
          : undefined;
        const lookup = await api.lookupPhoneNumber(transferTo, countryCode);
        const badFormat = lookup.statusCode === 400 || lookup.statusCode === 404;
        if ((lookup.success && lookup.data?.valid === false) || badFormat) {
          console.error('[CallStore] Transfer target failed lookup:', transferTo);
          toast.error(`Transfer failed — "${target}" is not a valid phone number`);
          return;
        }
        // Use the normalized E.164 for the actual dial when Lookup provides it
        if (lookup.success && lookup.data?.phone_number) {
          phoneTarget = lookup.data.phone_number;
        }
      }
      // All calls use conferences — find the conference this agent is in.
      // Participant lookups run in parallel (was: sequential N+1).
      //
      // FAILURES HERE MUST ABORT, not fall through. proxyRequest never throws
      // — a 429/timeout comes back as { success: false } — and treating that
      // as "agent is not in any conference" sent a conferenced call down the
      // park-customer fallback: the customer got yanked to hold music and the
      // agent's leg redirected mid-sentence. Under load (several agents
      // transferring at once in a busy org) a 429 is an expected outcome.
      const confResp = await api.getConferences(accountSid, 'in-progress');
      if (!confResp.success) {
        fail('list conferences', confResp.error);
        return;
      }
      const conferences = confResp.data?.conferences || [];

      // Batches of 20 with early exit — an unbounded fan-out over hundreds of
      // conferences would trip Twilio's concurrent-request limit (429s).
      // Record the leg the target answered on, against the call. Twilio gives a
      // participant-dialled leg no parent link, so this is how call history
      // later finds notes the transferred-to agent writes under it. BOTH
      // transfer paths must do it: a never-held call takes the park path below,
      // which is the ordinary case, and it used to record nothing at all —
      // leaving note recovery to a heuristic that could pick the wrong call.
      // Fire-and-forget: a failure here must never affect the transfer itself.
      const recordTransferLeg = (targetLeg: string | undefined | null) => {
        if (!targetLeg) return;
        const existingLegs = get().callMetadata[callSid]?.transferLegs || [];
        get().updateCallMetadata(
          callSid,
          { transferLegs: [...existingLegs.filter((l) => l.sid !== targetLeg), { sid: targetLeg, agent: target.trim() }] },
          call.customerNumber
        ).catch(() => { /* non-blocking */ });
      };

      let targetConf: string | null = null;
      let scanFailed = false;
      for (let i = 0; i < conferences.length && !targetConf; i += 20) {
        const batch = conferences.slice(i, i + 20);
        const matches = await Promise.all(
          batch.map(async (conf) => {
            const pResp = await api.getConferenceParticipants(accountSid, conf.sid);
            if (!pResp.success) { scanFailed = true; return null; }
            const participants = pResp.data?.participants || [];
            return participants.some((p) => p.call_sid === callSid) ? conf.sid : null;
          })
        );
        targetConf = matches.find((sid) => sid !== null) || null;
      }
      // Not found, but part of the scan errored — the agent may well BE in a
      // conference we failed to read. Don't guess; the call is untouched.
      if (!targetConf && scanFailed) {
        fail('scan conferences', 'participant lookups failed (rate limit?) — try again');
        return;
      }

      const dialTo = isClient ? transferTo : phoneTarget;

      // Poll a freshly-dialed leg until it answers or terminally fails.
      // A format-valid but dead/unanswered number can only be detected here —
      // every earlier API step "succeeds" because the request itself is valid.
      const waitForAnswer = async (legSid: string, timeoutMs = 25_000): Promise<boolean> => {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 1500));
          const leg = await api.getCallById(accountSid, legSid);
          const st = leg.data?.status;
          if (st === 'in-progress') return true;
          if (st === 'busy' || st === 'failed' || st === 'no-answer' || st === 'canceled' || st === 'completed') return false;
          // queued / ringing → keep polling
        }
        // Timed out — stop the still-ringing leg so it can't connect later
        await api.updateCall(accountSid, legSid, 'Status=completed');
        return false;
      };

      if (targetConf) {
        // Agent's call is already a conference: ring the target INTO it and
        // only step out after they actually answer.
        get()._updateCallStatus(callId, 'transferring');
        const addResp = await api.addConferenceParticipant(
          accountSid,
          targetConf,
          callerIdForTransfer,
          dialTo,
          // EndConferenceOnExit is REQUIRED — Twilio defaults it to false, so
          // without it the conference (and the customer's leg) outlives the
          // receiving agent hanging up: the app shows "ended" while the caller
          // is still connected to silence. The other transfer path already
          // sets it; this one was missed.
          'Timeout=25&EndConferenceOnExit=true&Beep=false'
        );
        if (!addResp.success) {
          get()._updateCallStatus(callId, 'connected');
          fail('add participant', addResp.error);
          return;
        }
        const targetLeg = addResp.data?.call_sid;
        if (targetLeg) {
          const answered = await waitForAnswer(targetLeg);
          if (!answered) {
            // Target never picked up — drop their ringing leg; the agent is
            // still in the conference with the customer.
            await api.removeConferenceParticipant(accountSid, targetConf, targetLeg);
            get()._updateCallStatus(callId, 'connected');
            fail('target did not answer');
            return;
          }
        }
        recordTransferLeg(targetLeg);
        // Past this point the target is IN the conference with
        // EndConferenceOnExit=true. Abandoning the transfer without taking them
        // back out leaves a third party who can end the customer's call by
        // hanging up — so every abort below drops them first. Best-effort: the
        // agent keeping the caller matters more than this tidy-up succeeding.
        const dropTarget = async () => {
          if (!targetLeg) return;
          await api
            .removeConferenceParticipant(accountSid, targetConf, targetLeg)
            .catch(() => { /* nothing further we can do */ });
        };
        const keepAliveResp = await api.updateConferenceParticipant(accountSid, targetConf, callSid, 'EndConferenceOnExit=false');
        if (!keepAliveResp.success) {
          await dropTarget();
          get()._updateCallStatus(callId, 'connected');
          fail('keep conference alive', keepAliveResp.error);
          return;
        }
        const removeResp = await api.removeConferenceParticipant(accountSid, targetConf, callSid);
        if (!removeResp.success) {
          await dropTarget();
          // The agent's own EndConferenceOnExit was cleared above; put it back,
          // or the customer outlives the agent hanging up.
          await api
            .updateConferenceParticipant(accountSid, targetConf, callSid, 'EndConferenceOnExit=true')
            .catch(() => { /* best effort */ });
          get()._updateCallStatus(callId, 'connected');
          fail('remove agent leg', removeResp.error);
          return;
        }
      } else {
        // No conference: the old approach redirected the customer STRAIGHT to
        // the target, so a failed dial killed their call with nobody left to
        // help. Instead: park the customer in a private conference (hold
        // music), ring the target into it, and if they don't answer ring the
        // AGENT back into the same conference.
        const callDetails = await api.getCallById(accountSid, callSid);
        if (!callDetails.success) {
          fail('lookup call', callDetails.error);
          return;
        }
        const parentCallSid = callDetails.data?.parent_call_sid;

        // The customer leg: the parent for inbound, the in-progress child for outbound
        let customerLeg = parentCallSid || null;
        if (!customerLeg) {
          const childResp = await api.getChildCalls(accountSid, callSid);
          const activeChild = (childResp.data?.calls || []).find((c) => c.status === 'in-progress');
          if (!activeChild) {
            fail('no active leg', childResp.error || 'no in-progress child call');
            return;
          }
          customerLeg = activeChild.sid;
        }

        const confName = `ua_transfer_${callSid}`;
        const parkTwiml = `<Response><Dial><Conference endConferenceOnExit="true" beep="false">${confName}</Conference></Dial></Response>`;
        get()._updateCallStatus(callId, 'transferring');
        const redirectResp = await api.updateCall(accountSid, customerLeg, `Twiml=${encodeURIComponent(parkTwiml)}`);
        // Remember who is waiting, so ending the call releases them too.
        if (redirectResp.success) set({ _parkedCustomerLeg: customerLeg });
        if (!redirectResp.success) {
          get()._updateCallStatus(callId, 'connected');
          fail('park customer', redirectResp.error);
          return;
        }

        // Recover path: ring the agent back into the conference
        const recoverAgent = async (confSid: string) => {
          const agentEmail = useAuthStore.getState().user?.email;
          if (agentEmail) {
            await api.addConferenceParticipant(
              accountSid, confSid, callerIdForTransfer, `client:${agentEmail}`, 'EndConferenceOnExit=true&Beep=false'
            );
          }
          get()._updateCallStatus(callId, 'ended');
          get()._stopDurationTimer(callId);
          toast.error('Transfer failed — reconnecting the caller to you, please answer');
        };

        // Find the conference the customer just landed in (few-second poll)
        let confSid: string | null = null;
        for (let tries = 0; tries < 6 && !confSid; tries++) {
          const found = await api.getConferenceByName(accountSid, confName);
          confSid = found.data?.conferences?.[0]?.sid || null;
          if (!confSid) await new Promise((r) => setTimeout(r, 1000));
        }
        if (!confSid) {
          // The customer is already parked and the agent's leg died with the
          // redirect, so simply giving up abandons the caller on hold music
          // with nobody coming. Ring the agent back the direct way instead —
          // this needs no conference lookup, which is the thing that just
          // failed. `customerLeg` is the leg now sitting in the conference.
          const agentEmail = useAuthStore.getState().user?.email;
          let recovered = false;
          if (agentEmail && customerLeg) {
            const backTwiml = `<Response><Dial><Client>${agentEmail}</Client></Dial></Response>`;
            const backResp = await api.updateCall(
              accountSid, customerLeg, `Twiml=${encodeURIComponent(backTwiml)}`
            );
            recovered = backResp.success;
          }
          get()._updateCallStatus(callId, 'ended');
          get()._stopDurationTimer(callId);
          if (recovered) {
            toast.error('Transfer failed — reconnecting the caller to you, please answer');
          } else {
            // Say what actually happened. The generic message claims the agent
            // still has the caller, which is false on this path.
            console.error('[CallStore] Transfer failed and the caller could not be reconnected', { confName, customerLeg });
            toast.error('Transfer failed and the caller could not be reconnected — please call them back');
          }
          return;
        }

        const addResp = await api.addConferenceParticipant(
          accountSid, confSid, callerIdForTransfer, dialTo, 'Timeout=25&EndConferenceOnExit=true&Beep=false'
        );
        if (!addResp.success) {
          await recoverAgent(confSid);
          return;
        }
        const targetLeg = addResp.data?.call_sid;
        if (targetLeg) {
          const answered = await waitForAnswer(targetLeg);
          if (!answered) {
            await recoverAgent(confSid);
            return;
          }
          recordTransferLeg(targetLeg);
        }
      }

      // Only reached when the target genuinely joined the call. The caller is
      // with them now, so they must NOT be hung up when this agent's widget
      // closes.
      set({ _parkedCustomerLeg: null });
      get()._updateCallStatus(callId, 'transferred');
      get()._stopDurationTimer(callId);
    } catch (err) {
      console.error('[CallStore] Transfer failed:', err);
      toast.error('Transfer failed — you are still connected to the caller');
    }
  },

  /**
   * Conference call (KAN-180) — ring a phone number or agent email INTO the
   * live call. Unlike transfer, the agent stays on the line.
   *
   * The call must live in a Twilio conference first:
   *  - a plain <Dial> bridge is moved into one with the existing hold dance
   *    (hold → resume). The customer hears hold music for a second or two.
   *  - after a hold, the call is already in the hold room conference.
   *  - a leg received through a transfer was dialled INTO a conference, so we
   *    find that conference by scanning for this leg.
   * The added person joins with EndConferenceOnExit=false: when they hang up
   * the call goes on. The agent's own leg keeps EndConferenceOnExit=true, so
   * the agent hanging up still ends the call for everyone.
   */
  addCallParticipant: async (callId, target) => {
    const { api, accountSid } = get();
    if (!api || !accountSid) return;
    const getCall = () => get().activeCalls.find((c) => c.id === callId);
    const call = getCall();
    if (!call || call.addingParticipant) return;
    if (call.isOnHold || call.holdPending) {
      toast.error('Resume the call before adding someone');
      return;
    }

    const trimmed = target.trim();
    if (!trimmed) return;
    const isClient = trimmed.includes('@');
    const agentSid = call.tpaCallId || callId;
    const callerId = toDialableCallerId(call.companyCallerId) || toDialableCallerId(call.customerNumber);
    if (!callerId) {
      toast.error('Could not add — no caller ID available for this call');
      return;
    }

    const setExtra = (extra: Partial<ActiveCall>) => {
      const fresh = getCall();
      if (fresh) get()._updateCallStatus(callId, fresh.status, extra);
    };
    const callIsLive = () => {
      const fresh = getCall();
      return !!fresh && (fresh.status === 'connected' || fresh.status === 'on-hold');
    };

    setExtra({ addingParticipant: true });
    try {
      // ── 1. Check the target before touching the call ─────────────
      let dialTo: string;
      if (isClient) {
        const selfEmail = useAuthStore.getState().user?.email;
        if (selfEmail && selfEmail.toLowerCase() === trimmed.toLowerCase()) {
          toast.error('You are already on this call');
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
          toast.error(`"${trimmed}" is not a valid agent email`);
          return;
        }
        dialTo = `client:${trimmed}`;
      } else {
        dialTo = resolveBareNumber(toDialE164(trimmed), parsePhoneNumberFromString(callerId)?.country);
        if (api.lookupPhoneNumber) {
          const countryCode = !dialTo.startsWith('+')
            ? parsePhoneNumberFromString(callerId)?.country
            : undefined;
          const lookup = await api.lookupPhoneNumber(dialTo, countryCode);
          const badFormat = lookup.statusCode === 400 || lookup.statusCode === 404;
          if ((lookup.success && lookup.data?.valid === false) || badFormat) {
            toast.error(`"${trimmed}" is not a valid phone number`);
            return;
          }
          if (lookup.success && lookup.data?.phone_number) dialTo = lookup.data.phone_number;
        }
      }
      // Compare what is actually dialled, not the typed text: "9876543210" and
      // "+91 98765 43210" ring the same phone.
      const now = getCall();
      if ((now?.conferenceParticipants || []).some((p) => p.dialTo.toLowerCase() === dialTo.toLowerCase())) {
        toast.error(`${trimmed} is already on this call`);
        return;
      }
      if (!isClient && now?.customerNumber) {
        const customer = resolveBareNumber(
          toDialableCallerId(now.customerNumber),
          parsePhoneNumberFromString(callerId)?.country
        );
        if (customer === dialTo) {
          toast.error(`${trimmed} is the caller — already on this call`);
          return;
        }
      }

      // ── 2. Make sure the call lives in a conference ───────────────
      const findConferenceByName = async (room: string): Promise<string | null> => {
        for (let attempt = 0; attempt < 4; attempt++) {
          const resp = await api.getConferences(accountSid, 'in-progress', room);
          const sid = resp.data?.conferences?.find((c) => c.friendly_name === room)?.sid;
          if (sid) return sid;
          await sleep(700);
        }
        return null;
      };
      const findConferenceOfLeg = async (): Promise<string | null> => {
        const confResp = await api.getConferences(accountSid, 'in-progress');
        const conferences = confResp.data?.conferences || [];
        // Batches of 20 with early exit — keeps clear of Twilio's 429s.
        for (let i = 0; i < conferences.length; i += 20) {
          const matches = await Promise.all(
            conferences.slice(i, i + 20).map(async (conf) => {
              const pResp = await api.getConferenceParticipants(accountSid, conf.sid);
              return (pResp.data?.participants || []).some((p) => p.call_sid === agentSid) ? conf.sid : null;
            })
          );
          const hit = matches.find((sid) => sid !== null);
          if (hit) return hit;
        }
        return null;
      };

      let fresh = getCall();
      let confSid: string | null = fresh?.conferenceSid || fresh?.holdConferenceSid || null;
      if (!confSid && fresh?.holdRoom && fresh.holdConferenceStarted) {
        confSid = await findConferenceByName(fresh.holdRoom);
      }
      if (!confSid && fresh && !fresh.holdRoom) {
        // A plain <Dial> bridge has a parent leg (inbound) or a live child (outbound).
        const legResp = await api.getCallById(accountSid, agentSid);
        if (!legResp.success) {
          toast.error('Could not add — please try again');
          return;
        }
        let bridged = !!legResp.data?.parent_call_sid;
        if (!bridged) {
          const kids = await api.getChildCalls(accountSid, agentSid);
          bridged = !!kids.data?.calls?.some((c) => c.status === 'in-progress');
        }
        if (bridged) {
          // holdCall shows its own error toast when a step fails.
          await get().holdCall(callId);
          if (!getCall()?.isOnHold) return;
          await get().holdCall(callId);
          fresh = getCall();
          if (!fresh?.holdConferenceStarted || !fresh.holdRoom) return;
          confSid = await findConferenceByName(fresh.holdRoom);
        } else {
          confSid = await findConferenceOfLeg();
        }
      }
      if (!callIsLive()) return;
      if (!confSid) {
        toast.error('Could not add — the call conference was not found. Please try again.');
        return;
      }
      setExtra({ conferenceSid: confSid });

      // The caller and added people join with EndConferenceOnExit=false, so
      // once they all hang up the agent sits alone in the room and the call
      // looks live forever. Watch the room and hang up when nobody else is left.
      if (!conferenceWatchers.has(callId)) {
        conferenceWatchers.add(callId);
        const watchConfSid = confSid;
        void (async () => {
          let emptyPolls = 0;
          try {
            while (callIsLive()) {
              await sleep(4000);
              if (!callIsLive()) return;
              const now = getCall();
              // Someone may be on the way in — don't count the room as empty yet.
              if (now?.holdPending || now?.addingParticipant || (now?.conferenceParticipants || []).some((p) => p.status === 'ringing')) {
                emptyPolls = 0;
                continue;
              }
              const resp = await api.getConferenceParticipants(accountSid, watchConfSid);
              if (!resp.success) continue;
              const others = (resp.data?.participants || []).filter((p) => p.call_sid !== agentSid);
              // Two empty reads in a row, so one stale read can't drop a live call.
              emptyPolls = others.length === 0 ? emptyPolls + 1 : 0;
              if (emptyPolls >= 2) {
                if (callIsLive()) get().endCall(callId);
                return;
              }
            }
          } finally {
            conferenceWatchers.delete(callId);
          }
        })();
      }

      // ── 3. Ring the new person into the conference ───────────────
      const addResp = await api.addConferenceParticipant(
        accountSid, confSid, callerId, dialTo, 'Timeout=25&EndConferenceOnExit=false&Beep=false'
      );
      const legSid = addResp.data?.call_sid;
      if (!addResp.success || !legSid) {
        console.error('[CallStore] Add participant failed:', addResp.error);
        toast.error(`Could not add ${trimmed}`);
        return;
      }
      setExtra({
        conferenceParticipants: [
          ...(getCall()?.conferenceParticipants || []),
          { callSid: legSid, target: trimmed, dialTo, status: 'ringing' },
        ],
      });

      // ── 4. Follow the new leg: ringing → joined → left ───────────
      void (async () => {
        const dropFromList = () =>
          setExtra({ conferenceParticipants: (getCall()?.conferenceParticipants || []).filter((p) => p.callSid !== legSid) });
        const stillListed = () => (getCall()?.conferenceParticipants || []).some((p) => p.callSid === legSid);
        const ringDeadline = Date.now() + 30_000;
        let joined = false;
        while (callIsLive() && stillListed()) {
          await sleep(joined ? 6000 : 1500);
          if (!callIsLive() || !stillListed()) return;
          const leg = await api.getCallById(accountSid, legSid);
          const st = leg.data?.status;
          if (st === 'in-progress') {
            if (!joined) {
              joined = true;
              setExtra({
                conferenceParticipants: (getCall()?.conferenceParticipants || []).map((p) =>
                  p.callSid === legSid ? { ...p, status: 'joined' as const } : p
                ),
              });
              toast.success(`${trimmed} joined the call`);
            }
            continue;
          }
          if (st === 'busy' || st === 'failed' || st === 'no-answer' || st === 'canceled' || st === 'completed') {
            dropFromList();
            if (joined) toast(`${trimmed} left the call`);
            else toast.error(`${trimmed} did not answer`);
            return;
          }
          if (!joined && Date.now() > ringDeadline) {
            // Stop the still-ringing leg so it can't connect later. If the stop
            // fails, keep them listed and loop again: the next poll retries the
            // stop, or shows them as Joined (with a remove button) if they answered.
            const stop = await api.updateCall(accountSid, legSid, 'Status=completed');
            if (!stop.success) continue;
            dropFromList();
            toast.error(`${trimmed} did not answer`);
            return;
          }
        }
      })();
    } catch (err) {
      console.error('[CallStore] Add participant failed:', err);
      toast.error('Could not add — you are still connected to the caller');
    } finally {
      setExtra({ addingParticipant: false });
    }
  },

  removeCallParticipant: async (callId, participantCallSid) => {
    const { api, accountSid } = get();
    const call = get().activeCalls.find((c) => c.id === callId);
    if (!api || !accountSid || !call) return;

    let removed = false;
    if (call.conferenceSid) {
      removed = (await api.removeConferenceParticipant(accountSid, call.conferenceSid, participantCallSid)).success;
    }
    if (!removed) {
      // Hanging up the leg itself works even when the conference lookup doesn't
      removed = (await api.updateCall(accountSid, participantCallSid, 'Status=completed')).success;
    }
    if (!removed) {
      toast.error('Could not remove this person — please try again');
      return;
    }
    const fresh = get().activeCalls.find((c) => c.id === callId);
    if (fresh) {
      get()._updateCallStatus(callId, fresh.status, {
        conferenceParticipants: (fresh.conferenceParticipants || []).filter((p) => p.callSid !== participantCallSid),
      });
    }
  },

  // ── UI Actions ──────────────────────────────────────────────────

  setDialerNumber: (number) => set({ dialerNumber: number }),
  setSelectedCallerId: (value) => set({ selectedCallerId: value }),
  setContextMenuCallNumber: (number) => set({ contextMenuCallNumber: number }),

  toggleCallManager: () =>
    set((s) => ({ callManagerOpen: !s.callManagerOpen })),
  toggleCrmCallManager: () =>
    set((s) => ({ crmCallManagerOpen: !s.crmCallManagerOpen })),
  dismissCall: (callId) =>
    set((s) => ({ dismissedCallIds: new Set([...s.dismissedCallIds, callId]) })),
  undismissAllCalls: () =>
    set((s) => {
      // Only bring back LIVE calls. Ended calls that were auto-dismissed stay
      // dismissed — otherwise reopening one live call would resurrect every
      // finished call of the session (and select the oldest dead one).
      const next = new Set<string>();
      for (const id of s.dismissedCallIds) {
        const call = s.activeCalls.find((c) => c.id === id);
        if (!call || CALL_STATUS_CONFIGS[call.status]?.callEnded) next.add(id);
      }
      return { dismissedCallIds: next };
    }),

  setCallManagerTab: (tab) => set({ callManagerTab: tab }),

  // ── Call History ────────────────────────────────────────────────

  fetchCallHistory: async (options) => {
    const { api, accountSid, nextHistoryPage } = get();
    if (!api || !accountSid) return;

    set({ callHistoryLoading: true });

    try {
      // Collect all raw calls from one or more API requests
      let allCalls: TwilioCallResource[] = [];
      let nextPage: string | null = null;

      // Build shared filter params for Twilio API
      // 100, not 20: Twilio pages by call RECORDS, and one conversation is
      // 4-6 of them (customer leg, agent leg, transfer leg, ring-back). A page
      // of 20 records is only ~3 conversations, so notes on the fourth-newest
      // call looked missing — their row simply was never fetched.
      const filterParts: string[] = ['PageSize=100'];
      if (options?.status) {
        filterParts.push(`Status=${encodeURIComponent(options.status)}`);
      }
      if (options?.dateFrom) {
        filterParts.push(`StartTime%3E=${encodeURIComponent(options.dateFrom + 'T00:00:00Z')}`);
      }
      if (options?.dateTo) {
        filterParts.push(`StartTime%3C=${encodeURIComponent(options.dateTo + 'T23:59:59Z')}`);
      }
      const filterQS = filterParts.join('&');

      if (options?.loadMore && nextHistoryPage) {
        const resp = await api.getCallsByUrl(nextHistoryPage);
        allCalls = resp.data?.calls || [];
        nextPage = resp.data?.next_page_uri || null;
      } else if (options?.search) {
        // Search both From and To (matches old twilio-call-things.js behaviour)
        const searchVal = encodeURIComponent(options.search);
        const [fromResp, toResp] = await Promise.all([
          api.getCalls(accountSid, `${filterQS}&From=${searchVal}`),
          api.getCalls(accountSid, `${filterQS}&To=${searchVal}`),
        ]);
        // Merge and deduplicate by SID
        const seen = new Set<string>();
        for (const c of [...(fromResp.data?.calls || []), ...(toResp.data?.calls || [])]) {
          if (!seen.has(c.sid)) {
            seen.add(c.sid);
            allCalls.push(c);
          }
        }
        nextPage = null; // No pagination for search results
      } else {
        const resp = await api.getCalls(accountSid, filterQS);
        allCalls = resp.data?.calls || [];
        nextPage = resp.data?.next_page_uri || null;
      }

      // Client-side direction filter (Twilio doesn't support direction as a query param directly)
      if (options?.direction) {
        allCalls = allCalls.filter((c) =>
          options.direction === 'inbound' ? c.direction === 'inbound' : c.direction.startsWith('outbound')
        );
      }

      if (allCalls.length === 0) {
        set((s) => ({
          callHistory: options?.loadMore ? s.callHistory : [],
          callHistoryHasMore: false,
          nextHistoryPage: null,
          callHistoryLoading: false,
        }));
        return;
      }

      // Pool every page fetched so far and rebuild from scratch. Appending
      // per-page rows meant a leg on page 2 could never join its call on
      // page 1 — the matcher only ever saw one page at a time.
      const prevPool = options?.loadMore ? get()._historyRawCalls : [];
      const poolMap = new Map<string, TwilioCallResource>();
      for (const c of [...prevPool, ...allCalls]) poolMap.set(c.sid, c);
      allCalls = [...poolMap.values()];
      // Linking mutates the records; clear the previous rebuild's links or
      // childCalls would accumulate duplicates.
      allCalls.forEach((c) => { c.childCalls = undefined; c.childCall = undefined; c.parentCall = undefined; });

      // Build call ID map for parent/child linking
      const callMap = new Map<string, TwilioCallResource>();
      allCalls.forEach((c) => callMap.set(c.sid, c));

      // Link parent/child. A transferred call has one child leg PER agent, so
      // collect them all — notes are stored against whichever leg the agent
      // who wrote them was on, and the history row has to read every one.
      allCalls.forEach((c) => {
        if (c.parent_call_sid) {
          const parent = callMap.get(c.parent_call_sid);
          if (parent) {
            (parent.childCalls ||= []).push(c);
            parent.childCall = c;
            c.parentCall = parent;
          }
        }
      });

      // A transfer target is dialled through the Participants API, so its leg
      // has NO parent_call_sid and the linking above cannot reach it. That leg
      // is where the agent who answered the transfer writes their notes, so
      // without it their own notes are missing from the call in history.
      //
      // The transferring agent records the leg in shared metadata, but that
      // write can fail, so recover the leg from the call list too: it dials out
      // to client:<agent> using the customer's number as the caller ID, which
      // makes it findable against the call that was in progress at the time.
      const startedAt = (v: string) => { const t = new Date(v).getTime(); return Number.isNaN(t) ? 0 : t; };
      // Who is looking. Twilio dials an agent as `client:<their email>`, so
      // this is how a leg is recognised as theirs.
      const viewerEmail = _viewerEmail();
      const clientIdentity = (v: string) => {
        const t = (v || '').toLowerCase();
        return t.startsWith('client:') ? t.slice('client:'.length) : '';
      };
      // A leg the transfer dialled carries no link back to its call, so it has
      // to be matched by hand. Only the VIEWER's own legs are matched, for two
      // reasons: notes are private, so a colleague's leg is of no use here
      // anyway, and it is what makes the match reliable.
      //
      // Matching used to key on the number the transfer dialled out with. On an
      // inbound call that is the COMPANY's number, identical on every inbound
      // call, so the match was ambiguous whenever two calls overlapped and the
      // leg was dropped — which is why a transferred agent's notes appeared
      // only sometimes. An agent is on one line per conversation, so their own
      // leg placed by timing is far more discriminating.
      //
      // This is a fallback. The transfer records the leg properly; that record
      // is currently dropped before it is stored, and until that is fixed this
      // is what finds the note. See docs/firestore-empty-cache-bug.md's
      // sibling issue for the server-side fix.
      const SLACK_MS = 120_000;
      const endsAt = (c: TwilioCallResource) =>
        startedAt(c.start_time) + (parseInt(c.duration || '0', 10) || 0) * 1000;
      const myOrphanLegs = viewerEmail
        ? allCalls.filter(
            (c) => !c.parent_call_sid && clientIdentity(c.to) === viewerEmail,
          )
        : [];
      myOrphanLegs.forEach((leg) => {
        const legStart = startedAt(leg.start_time);
        const legEnd = endsAt(leg);
        if (!legStart) return;
        // The leg lives INSIDE its conversation: it starts while the call is up
        // and finishes with it. Both ends must fit, which is what separates two
        // calls running at the same time.
        const candidates = allCalls.filter((c) => {
          if (c === leg || c.to.startsWith('client') || c.from.startsWith('client')) return false;
          const start = startedAt(c.start_time);
          if (!start) return false;
          const finish = endsAt(c) + SLACK_MS;
          return legStart >= start && legStart <= finish && legEnd <= finish;
        });
        if (candidates.length !== 1) {
          if (candidates.length > 1) {
            console.warn(
              `[CallStore] own leg ${leg.sid} fits ${candidates.length} calls — leaving it unattached rather than guessing`
            );
          }
          return;
        }
        // Only attach the leg — childCall drives the row's duration, status and
        // display SID, and must stay the leg the call was first answered on.
        // Landing in childCalls also puts it in myLegSids, which is what the
        // notes panel and the note count read.
        const parent = candidates[0];
        if (!(parent.childCalls || []).some((ch) => ch.sid === leg.sid)) {
          (parent.childCalls ||= []).push(leg);
        }
      });

      // Build structured call history
      const items: CallHistoryItem[] = [];
      allCalls.forEach((c) => {
        // Skip internal client-to-client routing calls
        if (c.from.startsWith('client') || c.to.startsWith('client')) return;

        const isIncoming = c.direction === 'inbound';
        let duration = c.duration;
        let status = c.status;

        // For incoming calls, use child call data for duration/status
        if (isIncoming && c.childCall) {
          if (c.childCall.duration && parseInt(c.childCall.duration) > 0) {
            duration = c.childCall.duration;
          }
          if (c.childCall.status === 'no-answer') {
            status = 'no-answer';
          }
        }
        if (isIncoming && !c.childCall && !c.parentCall) {
          status = 'no-answer';
        }
        if (isIncoming && status === 'canceled') {
          status = 'missed';
        }

        // Extract agent email from client calls
        let agentEmail: string | null = null;
        const childCall = c.childCall;
        if (childCall) {
          if (childCall.from.startsWith('client:') && childCall.from.includes('@')) {
            agentEmail = childCall.from.replace('client:', '');
          } else if (childCall.to.startsWith('client:') && childCall.to.includes('@')) {
            agentEmail = childCall.to.replace('client:', '');
          }
        }
        // For outbound calls: the parent call (client:agent → Twilio) has the agent info
        if (!agentEmail && c.parentCall) {
          if (c.parentCall.from.startsWith('client:') && c.parentCall.from.includes('@')) {
            agentEmail = c.parentCall.from.replace('client:', '');
          } else if (c.parentCall.to.startsWith('client:') && c.parentCall.to.includes('@')) {
            agentEmail = c.parentCall.to.replace('client:', '');
          }
        }

        // For incoming calls with a child, use the child SID as the display ID
        // so notes/recordings match the active call SID the agent sees
        const displaySid = (isIncoming && c.childCall) ? c.childCall.sid : c.sid;
        // Every leg this conversation touched, so the notes panel can gather
        // notes written by any agent who handled it (each writes under their
        // own leg, and a transfer creates a new one).
        const legSids = [...new Set([c.sid, ...(c.childCalls || []).map((ch) => ch.sid)])];
        // Which of those legs was the VIEWER on? Twilio dials an agent as
        // `client:<email>`, the identity the Voice SDK registers with, so any
        // note they wrote during the call sits on that leg. Knowing it lets the
        // notes badge read one or two documents instead of every leg.
        const myLegSids = viewerEmail
          ? [c, ...(c.parentCall ? [c.parentCall] : []), ...(c.childCalls || [])]
              .filter((l) => clientIdentity(l.to) === viewerEmail || clientIdentity(l.from) === viewerEmail)
              .map((l) => l.sid)
          : [];

        items.push({
          id: displaySid,
          legSids,
          myLegSids: myLegSids.length ? [...new Set(myLegSids)] : undefined,
          customerNumber: isIncoming ? c.from : c.to,
          companyCallerId: isIncoming ? c.to : c.from,
          isIncoming,
          duration: duration || '0',
          status,
          time: c.start_time,
          price: c.price ? `${c.price} ${c.price_unit}` : '--',
          agentEmail,
          parentCallSid: isIncoming && c.childCall ? c.sid : (c.parent_call_sid || undefined),
        });
      });

      set(() => ({
        // Always the full rebuild — the pool above already contains every page.
        callHistory: items,
        _historyRawCalls: allCalls,
        callHistoryHasMore: !!nextPage,
        nextHistoryPage: nextPage,
        callHistoryLoading: false,
      }));

      // Fetch account-level recordings once to know which calls have recordings
      // Fetch on initial load or first search (CRM context always searches by phone);
      // on loadMore, re-map existing recordings
      if (!options?.loadMore && get().accountRecordings.length === 0) {
        try {
          // Build bidirectional SID maps so recordings on either leg show on the history row
          const childToParent = new Map<string, string>();
          const parentToChild = new Map<string, string>();
          allCalls.forEach((c) => {
            if (c.childCall) {
              childToParent.set(c.childCall.sid, c.sid);
              parentToChild.set(c.sid, c.childCall.sid);
            }
          });

          const recResp = await api.getRecordings(accountSid, 200);
          const recMap: Record<string, true> = {};
          const cachedRecs: Record<string, CallRecording[]> = {};
          const recs = recResp.data?.recordings || [];

          // Build a set of all call history item IDs for quick lookup
          const historyIds = new Set(items.map(i => i.id));

          recs.forEach((r) => {
            if (!r.call_sid) return;

            const recording: CallRecording = {
              id: r.sid,
              duration: r.duration,
              url: r.media_url,
            };

            // Mark this call_sid as having recordings
            recMap[r.call_sid] = true;

            // Also mark parent call SID (recording on child → show on parent)
            const parentSid = childToParent.get(r.call_sid);
            if (parentSid) recMap[parentSid] = true;

            // Also mark child call SID (recording on parent → show on child in history)
            const childSid = parentToChild.get(r.call_sid);
            if (childSid) recMap[childSid] = true;

            // Find which SID is actually displayed in history for caching
            const displaySid = historyIds.has(r.call_sid) ? r.call_sid
              : parentSid && historyIds.has(parentSid) ? parentSid
              : childSid && historyIds.has(childSid) ? childSid
              : r.call_sid;

            if (!cachedRecs[displaySid]) cachedRecs[displaySid] = [];
            cachedRecs[displaySid].push(recording);
          });

          set({ callsWithRecordings: recMap, cachedRecordings: cachedRecs, accountRecordings: recs });
        } catch {
          // Non-critical — icon just won't show
        }
      } else if (get().accountRecordings.length > 0) {
        // Re-map existing recordings against updated items (for loadMore/search)
        const existingRecs = get().accountRecordings;
        const childToParent = new Map<string, string>();
        const parentToChild = new Map<string, string>();
        allCalls.forEach((c) => {
          if (c.childCall) {
            childToParent.set(c.childCall.sid, c.sid);
            parentToChild.set(c.sid, c.childCall.sid);
          }
        });
        const recMap = { ...get().callsWithRecordings };
        const cachedRecs = { ...get().cachedRecordings };
        const allItems = get().callHistory;
        const historyIds = new Set(allItems.map((i) => i.id));

        existingRecs.forEach((r) => {
          if (!r.call_sid) return;
          recMap[r.call_sid] = true;
          const parentSid = childToParent.get(r.call_sid);
          if (parentSid) recMap[parentSid] = true;
          const childSid = parentToChild.get(r.call_sid);
          if (childSid) recMap[childSid] = true;

          const recording = { id: r.sid, duration: r.duration, url: r.media_url };
          const displaySid = historyIds.has(r.call_sid) ? r.call_sid
            : parentSid && historyIds.has(parentSid) ? parentSid
            : childSid && historyIds.has(childSid) ? childSid
            : r.call_sid;
          if (!cachedRecs[displaySid]) cachedRecs[displaySid] = [];
          if (!cachedRecs[displaySid].some((cr) => cr.id === r.sid)) {
            cachedRecs[displaySid].push(recording);
          }
        });
        set({ callsWithRecordings: recMap, cachedRecordings: cachedRecs });
      }
    } catch (error) {
      console.error('[CallStore] Failed to fetch call history:', error);
      set({ callHistoryLoading: false });
    }
  },

  // ── Real-time call alerts from RTDB ──────────────────────────────

  subscribeToCallAlerts: (extensionName) => {
    const uid = getEffectiveUid();
    if (!uid || !extensionName) return;

    // Unsub existing first
    get().unsubscribeFromCallAlerts();

    const path = `/instantUserConversationMessageAlerts/${extensionName}/${uid}/`;
    const alertRef = ref(rtdb, path);

    // Listeners are unbounded — limitToLast(N) on key-ordered RTDB would
    // silently drop events for callSids that aren't lex-last in the
    // window, which on this path can be any of the user's recent calls.
    // Initial-sync bandwidth is bounded by path size; path size is kept
    // small by the cleanup_realtime_alerts CF call fired from the L2
    // background sync at +10s after page load (use-app-init.ts).

    const seenKeys = new Set<string>();
    let initialBatchDone = false;

    // RTDB fires all existing children synchronously on attach.
    // Use requestAnimationFrame to mark when the initial batch is done.
    requestAnimationFrame(() => { initialBatchDone = true; });

    const unsub = onChildAdded(alertRef, (snapshot) => {
      const key = snapshot.key;
      if (!key) return;

      // Skip if we already processed this key
      if (seenKeys.has(key)) return;
      seenKeys.add(key);

      // Skip the initial batch of existing children
      if (!initialBatchDone) return;

      const val = snapshot.val();
      if (!val || val.c !== 'call-add') return;

      console.log('[CallStore] Real-time call alert:', val.a);

      const body = val.z || {};
      const callSid = val.a || body.CallSid || snapshot.key;
      const from = body.From || body.Caller || '';
      const to = body.To || body.Called || '';
      // If from starts with 'client:' it's an outbound call from the browser
      const isIncoming = from.startsWith('client:') ? false : (body.Direction === 'inbound');

      // Build a CallHistoryItem from the webhook body
      const newItem: CallHistoryItem = {
        id: callSid,
        customerNumber: isIncoming ? from : to,
        companyCallerId: isIncoming ? to : from,
        isIncoming,
        duration: body.CallDuration || body.Duration || '0',
        status: body.CallStatus || 'completed',
        time: new Date().toISOString(),
        price: '--',
        agentEmail: null,
        parentCallSid: body.ParentCallSid || undefined,
      };

      // Add or update in call history
      const { callHistory } = get();
      const existingIdx = callHistory.findIndex((c) => c.id === callSid);
      if (existingIdx >= 0) {
        // Update existing call (status change, duration update)
        const updated = [...callHistory];
        updated[existingIdx] = {
          ...updated[existingIdx],
          status: newItem.status,
          duration: newItem.duration !== '0' ? newItem.duration : updated[existingIdx].duration,
        };
        set({ callHistory: updated });
      } else {
        // New call — prepend
        set({ callHistory: [newItem, ...callHistory] });
      }
    }, (err) => {
      console.error('[CallStore] Call alert listener error:', err.message);
    });

    // Catches mid-flight status updates: CF re-sets the same callSid-keyed
    // child when call status transitions (ringing → completed, etc.).
    const unsub2 = onChildChanged(alertRef, (snapshot) => {
      const val = snapshot.val();
      if (!val || val.c !== 'call-add') return;

      const body = val.z || {};
      const callSid = val.a || body.CallSid || snapshot.key;
      const newStatus = body.CallStatus;

      if (newStatus && callSid) {
        const { callHistory } = get();
        const idx = callHistory.findIndex((c) => c.id === callSid);
        if (idx >= 0) {
          const updated = [...callHistory];
          updated[idx] = { ...updated[idx], status: newStatus, duration: body.CallDuration || body.Duration || updated[idx].duration };
          set({ callHistory: updated });
        }
      }
    });

    // Store unsub refs in a closure accessible by unsubscribe
    (get() as any)._callAlertUnsubs = [unsub, unsub2];
    (get() as any)._callAlertRef = alertRef;
  },

  unsubscribeFromCallAlerts: () => {
    const unsubs = (get() as any)._callAlertUnsubs as Unsubscribe[] | undefined;
    const alertRef = (get() as any)._callAlertRef;
    if (unsubs) {
      unsubs.forEach((u) => u());
      (get() as any)._callAlertUnsubs = undefined;
    }
    if (alertRef) {
      off(alertRef);
      (get() as any)._callAlertRef = undefined;
    }
  },

  fetchCallRecordings: async (callId) => {
    // First check cached recordings (from account-level fetch)
    const cached = get().cachedRecordings[callId];
    if (cached && cached.length > 0) return cached;

    // If account recordings were fetched and this call isn't in the map, skip API call
    const { accountRecordings, callsWithRecordings } = get();
    if (accountRecordings.length > 0 && !callsWithRecordings[callId]) {
      return [];
    }

    // Fallback: fetch per-call (may not find if recording is on child call)
    const { api, accountSid } = get();
    if (!api || !accountSid) return [];

    try {
      const resp = await api.getCallRecordings(accountSid, callId);
      if (!resp.data?.recordings) return [];

      return resp.data.recordings.map((r) => ({
        id: r.sid,
        duration: r.duration,
        url: r.media_url,
      }));
    } catch {
      return [];
    }
  },

  // ── Custom Call Labels (admin only) ────────────────────────────

  fetchCustomCallLabels: async () => {
    const docPath = _getPersonalSettingsDocPath();
    if (!docPath) return;

    try {
      const snap = await getDoc(doc(db, docPath));
      if (snap.exists()) {
        const data = snap.data();
        const labels = (data.ua_custom_call_labels || []) as import('@/types/voice').CallLabel[];
        set({ customCallLabels: labels });
      }
    } catch (err) {
      console.error('[CallStore] Failed to fetch custom labels:', err);
    }
  },

  saveCustomCallLabel: async (label) => {
    const docPath = _getPersonalSettingsDocPath();
    if (!docPath) return;

    const existing = get().customCallLabels;
    const idx = existing.findIndex((l) => l.value === label.value);
    const updated = idx >= 0
      ? existing.map((l, i) => i === idx ? label : l)
      : [...existing, { ...label, isCustom: true }];

    set({ customCallLabels: updated });

    try {
      await setDoc(doc(db, docPath), { ua_custom_call_labels: updated }, { merge: true });
    } catch (err) {
      console.error('[CallStore] Failed to save custom label:', err);
      set({ customCallLabels: existing });
    }
  },

  deleteCustomCallLabel: async (value) => {
    const docPath = _getPersonalSettingsDocPath();
    if (!docPath) return;

    const existing = get().customCallLabels;
    const updated = existing.filter((l) => l.value !== value);

    set({ customCallLabels: updated });

    try {
      await setDoc(doc(db, docPath), { ua_custom_call_labels: updated }, { merge: true });
    } catch (err) {
      console.error('[CallStore] Failed to delete custom label:', err);
      set({ customCallLabels: existing });
    }
  },

  // ── Call Metadata (lazy load from Firestore) ───────────────────

  fetchCallMetadataBatch: async (callSids) => {
    const uid = getEffectiveUid();
    const extensionName = useAppStore.getState().extensionName;
    if (!uid || !extensionName || callSids.length === 0) return;

    // Only fetch ones we haven't loaded yet
    const existing = get().callMetadata;
    const toFetch = callSids.filter((sid) => !existing[sid]);
    if (toFetch.length === 0) return;

    const me = _viewerEmail();
    const clean = (v: string) => v.replace(/[^a-z0-9_]+/gi, '');

    /** The badge counts the notes this viewer can actually open, so it has to
     *  count the same notes the panel lists.
     *
     *  A viewer's notes reach only two kinds of document: the leg they were on
     *  (written during the call) and the row itself (written from history).
     *  Reading just those keeps the badge to a couple of documents instead of
     *  every leg of a transferred call. The row's stored `notesCount` is no use
     *  here — it counts every author's notes, including colleagues' notes this
     *  viewer is not allowed to see.
     *
     *  Read from the server: a cached copy predating the note shows a stale
     *  count that survives a page reload.
     */
    const countMyNotes = async (
      rowSid: string,
      transferLegs?: Array<{ sid: string; agent: string }>,
    ): Promise<number> => {
      const row = get().callHistory.find((r) => r.id === rowSid);
      // A transfer records the leg it dialled, which is another place this
      // viewer's notes can be — and the only one available when the call is
      // not in the loaded history.
      const myTransferLegs = (transferLegs || [])
        .filter((l) => (l.agent || '').toLowerCase() === me)
        .map((l) => l.sid);
      // `parentCallSid` matters for an OUTBOUND call: notes are written under
      // the leg the Voice SDK gave the agent (client:<agent> → Twilio), and the
      // history row is the child leg. myLegSids only resolves that parent when
      // Twilio returned it in the same page, which the phone-number search
      // never does — so without this an agent's own outbound notes stay
      // invisible there. Reading it is safe: the author filter still decides
      // what counts.
      const candidates = [...new Set([
        rowSid,
        ...(row?.myLegSids || []),
        ...(row?.parentCallSid ? [row.parentCallSid] : []),
        ...myTransferLegs,
      ])].filter(Boolean);
      try {
        const counts = await Promise.all(
          candidates.map(async (candidate) => {
            const col = collection(db, `ulgebraUsers/${uid}/extensions/${extensionName}/UA_CALLS_INBOX/${clean(candidate)}/notes`);
            // COUNT the notes instead of downloading them — one billed read
            // per candidate rather than one per note document. The '' in the
            // filter keeps the badge fail-open like the panel: an unattributed
            // note counts as the viewer's. (A note MISSING the field entirely
            // cannot be matched by any filter; the panel still shows it and
            // its write-back corrects the badge on open.)
            const snap = me
              ? await getCountFromServer(query(col, where('authorEmail', 'in', [me, ''])))
              : await getCountFromServer(col);
            return snap.data().count;
          })
        );
        return counts.reduce((total, n) => total + n, 0);
      } catch (err) {
        console.error('[CallStore] Failed to count notes for', rowSid, err);
        return 0;
      }
    };

    try {
      const results: Record<string, import('@/types/voice').CallMetadata> = {};
      // Fetch in parallel (max 10 at a time)
      const batches = [];
      for (let i = 0; i < toFetch.length; i += 10) {
        batches.push(toFetch.slice(i, i + 10));
      }
      for (const batch of batches) {
        const promises = batch.map(async (sid) => {
          const cleanSid = sid.replace(/[^a-z0-9_]+/gi, '');
          const docRef = doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/UA_CALLS_INBOX/${cleanSid}`);
          // Server read for the same reason as the notes above: this document
          // is created during the call, so a cached "does not exist" is very
          // often just "not fetched yet" and would blank the label and count.
          const snap = await getDocFromServer(docRef);
          if (snap.exists()) {
            const data = snap.data();
            const transferLegs = (data.transferLegs || []) as Array<{ sid: string; agent: string }>;
            results[sid] = {
              notesCount: await countMyNotes(sid, transferLegs),
              label: data.label || data.labels?.[0] || null,
              assignee: data.assignee || null,
              customerNumber: data.customerNumber,
              transferLegs: transferLegs.length ? transferLegs : undefined,
            };
          } else {
            // No metadata document, but the viewer may still have notes on
            // their own leg — that leg is a different document.
            results[sid] = { notesCount: await countMyNotes(sid), label: null, assignee: null };
          }
        });
        await Promise.all(promises);
      }
      set((s) => ({ callMetadata: { ...s.callMetadata, ...results } }));
    } catch (err) {
      console.error('[CallStore] Failed to fetch call metadata:', err);
    }
  },

  updateCallMetadata: async (callSid, updates, customerNumber) => {
    const extensionName = useAppStore.getState().extensionName;
    const appStore = useAppStore.getState();
    const authState = useAuthStore.getState();
    if (!extensionName) return;

    // Optimistic update
    const existing = get().callMetadata[callSid] || { notesCount: 0, label: null, assignee: null };
    const updated = { ...existing, ...updates };
    set((s) => ({ callMetadata: { ...s.callMetadata, [callSid]: updated } }));

    try {
      await callFunction('new_callServiceAuthorizedAPI', {
        appCode: extensionName,
        orgId: appStore.appConfig?.orgId,
        TPAService: extensionName.includes('for') ? extensionName.split('for')[0] : extensionName,
        action: 'UA_INBOX_UPDATE_CALL_METADATA',
        data: {
          callId: callSid,
          customerNumber: customerNumber || '',
          ...updates,
        },
        _ua_lic_adminUserID: authState.selectedAdminUserId || undefined,
      });
    } catch (err) {
      console.error('[CallStore] Failed to update call metadata:', err);
      // Revert
      set((s) => ({ callMetadata: { ...s.callMetadata, [callSid]: existing } }));
    }
  },

  // ── Call Notes ─────────────────────────────────────────────────
  // Notes are stored as subcollection: UA_CALLS_INBOX/{callId}/notes/{noteId}

  fetchCallNotes: async (callSid, extraSids, ownLeg) => {
    const uid = getEffectiveUid();
    const extensionName = useAppStore.getState().extensionName;
    if (!uid || !extensionName) return;

    try {
      // A call can span several legs — one per agent that handled it after a
      // transfer — and each agent's notes are stored under their own leg. Read
      // every known leg and merge, or an agent cannot even see the note they
      // wrote themselves once the call is in history.
      const sids = [...new Set([callSid, ...(extraSids || [])])].filter(Boolean);
      const perSid = await Promise.all(
        sids.map(async (sid) => {
          const cleanCallSid = sid.replace(/[^a-z0-9_]+/gi, '');
          const colRef = collection(db, `ulgebraUsers/${uid}/extensions/${extensionName}/UA_CALLS_INBOX/${cleanCallSid}/notes`);
          // Server read on purpose. A note is usually opened seconds after it
          // was written, and the cache-first helper answers an unread
          // subcollection with "empty" — which is how a note the agent had
          // just written came back as "No notes yet".
          const snap = await getDocsFromServer(colRef);
          const out: import('@/types/voice').CallNote[] = [];
          snap.forEach((d) => {
            const data = d.data();
            out.push({
              id: data.id || d.id,
              text: data.text || '',
              authorEmail: data.authorEmail || '',
              authorName: data.authorName || data.authorEmail?.split('@')[0] || '',
              createdAt: data.createdAt || data.ct || 0,
            });
          });
          return out;
        })
      );
      const byId = new Map<string, import('@/types/voice').CallNote>();
      for (const n of perSid.flat()) byId.set(n.id, n);
      // Notes are PRIVATE to their author — but never at the cost of hiding an
      // agent's own note.
      //
      // Two rules, both deliberately fail-open:
      //  1. Reading a SINGLE leg (the live widget reads the agent's own leg)
      //     returns everything found there. Those notes are the agent's by
      //     construction, so filtering can only do harm — the author is
      //     assigned server-side and any mismatch would blank their own note.
      //  2. Reading SEVERAL legs (call history) hides a note only when it is
      //     positively attributed to somebody else. An unattributed note stays
      //     visible rather than vanishing.
      const me = _viewerEmail();
      // Only the agent's own live leg is exempt, and the CALLER says so — it
      // used to be inferred from `sids.length <= 1`, which is a property of
      // what the caller passed, not of whose leg it is. Any history row with no
      // sibling leg also collapses to one sid, and that silently turned the
      // privacy filter off and showed colleagues' notes.
      const notes = [...byId.values()]
        .filter((n) => ownLeg === true || _isMyNote(n, me))
        .sort((a, b) => b.createdAt - a.createdAt);
      // Keep the badge in step with what this agent can actually see, or a
      // row shows a count that includes colleagues' hidden notes.
      set((s) => {
        const meta = s.callMetadata[callSid];
        return {
          callNotes: { ...s.callNotes, [callSid]: notes },
          callMetadata: meta && meta.notesCount !== notes.length
            ? { ...s.callMetadata, [callSid]: { ...meta, notesCount: notes.length } }
            : s.callMetadata,
        };
      });
    } catch (err) {
      console.error('[CallStore] Failed to fetch call notes:', err);
    }
  },

  addCallNote: async (callSid, text, customerNumber) => {
    const extensionName = useAppStore.getState().extensionName;
    const appStore = useAppStore.getState();
    const authState = useAuthStore.getState();
    const userEmail = authState.user?.email || '';
    const userName = authState.user?.displayName || userEmail.split('@')[0];
    if (!extensionName || !text.trim()) return;

    const note: import('@/types/voice').CallNote = {
      id: `note_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      text: text.trim(),
      authorEmail: userEmail,
      authorName: userName,
      createdAt: Date.now(),
    };

    // Optimistic update — notes list + metadata count
    const existing = get().callNotes[callSid] || [];
    const updated = [note, ...existing];
    const currentMeta = get().callMetadata[callSid];
    const metadataUpdates: Record<string, import('@/types/voice').CallMetadata> = {
      [callSid]: {
        ...(currentMeta || { notesCount: 0, label: null, assignee: null }),
        notesCount: (currentMeta?.notesCount || 0) + 1,
      },
    };

    // Also update metadata for the call list entry if it uses a different SID
    // (e.g. call list shows child SID, but note is saved under parent SID)
    const { callHistory } = get();
    const childEntry = callHistory.find((c) => c.parentCallSid === callSid);
    if (childEntry) {
      const childMeta = get().callMetadata[childEntry.id];
      metadataUpdates[childEntry.id] = {
        ...(childMeta || { notesCount: 0, label: null, assignee: null }),
        notesCount: (childMeta?.notesCount || 0) + 1,
      };
    }

    // Kept so a rejected save can undo the count as well as the note — a badge
    // left one too high sticks for the rest of the session.
    const metaBeforeBump = Object.fromEntries(
      Object.keys(metadataUpdates).map((key) => [key, get().callMetadata[key]]),
    );
    const revertOptimistic = () => set((s) => {
      const callMetadata = { ...s.callMetadata };
      for (const [key, before] of Object.entries(metaBeforeBump)) {
        if (before) callMetadata[key] = before;
        else delete callMetadata[key];
      }
      return { callNotes: { ...s.callNotes, [callSid]: existing }, callMetadata };
    });

    set((s) => ({
      callNotes: { ...s.callNotes, [callSid]: updated },
      callMetadata: { ...s.callMetadata, ...metadataUpdates },
    }));

    try {
      // Call CF to add note (handles admin UID resolution + Firestore write).
      // NOTE: the CF REQUIRES a non-empty customerNumber and returns
      // {status:'error'} (HTTP 200) when anything is missing — check the
      // envelope, or a failed save silently vanishes on the next reload.
      const resp = (await callFunction('new_callServiceAuthorizedAPI', {
        appCode: extensionName,
        orgId: appStore.appConfig?.orgId,
        TPAService: extensionName.includes('for') ? extensionName.split('for')[0] : extensionName,
        action: 'UA_INBOX_ADD_CALL_NOTE',
        data: {
          callId: callSid,
          customerNumber: customerNumber || get().callMetadata[callSid]?.customerNumber || '',
          noteText: text.trim(),
        },
        _ua_lic_adminUserID: authState.selectedAdminUserId || undefined,
      })) as Record<string, unknown> | null;
      if (resp?.status === 'error' || resp?.userResponse === false) {
        console.error('[CallStore] Server rejected call note:', resp?.message ?? resp);
        revertOptimistic();
        toast.error('Failed to save note — please try again');
      }
    } catch (err) {
      console.error('[CallStore] Failed to add call note:', err);
      revertOptimistic();
      toast.error('Failed to save note — please try again');
    }
  },

  // ── Link / Unlink Numbers ──────────────────────────────────────

  linkNumber: async (phoneNumberSid) => {
    const { api, accountSid, twiMLAppId, allVoiceNumbers } = get();
    if (!api || !accountSid || !twiMLAppId) return;

    set({ linkingNumberSid: phoneNumberSid });
    try {
      // Enable incoming call handling — set TwiML app on the number
      const resp = await api.updatePhoneNumber(accountSid, phoneNumberSid, {
        VoiceApplicationSid: twiMLAppId,
      });
      if (resp.error) {
        console.error('[CallStore] Failed to enable incoming:', resp.error);
        set({ linkingNumberSid: null });
        return;
      }

      // Update linked flag (controls incoming handling only; caller ID is always available)
      const updatedNumbers = allVoiceNumbers.map((n) =>
        n.sid === phoneNumberSid
          ? { ...n, linked: true, voiceApplicationSid: twiMLAppId }
          : n
      );

      set({
        allVoiceNumbers: updatedNumbers,
        linkingNumberSid: null,
      });
      removeStorageItem('voice_numbers_cache'); // Invalidate cache
      console.log('[CallStore] Enabled incoming for number:', phoneNumberSid);
    } catch (error) {
      console.error('[CallStore] Failed to enable incoming:', error);
      set({ linkingNumberSid: null });
    }
  },

  unlinkNumber: async (phoneNumberSid) => {
    const { api, accountSid, allVoiceNumbers } = get();
    if (!api || !accountSid) return;

    set({ linkingNumberSid: phoneNumberSid });
    try {
      // Disable incoming call handling — clear TwiML app from the number
      const resp = await api.updatePhoneNumber(accountSid, phoneNumberSid, {
        VoiceApplicationSid: '',
      });
      if (resp.error) {
        console.error('[CallStore] Failed to disable incoming:', resp.error);
        set({ linkingNumberSid: null });
        return;
      }

      // Update linked flag; number stays in callerIds for outgoing
      const updatedNumbers = allVoiceNumbers.map((n) =>
        n.sid === phoneNumberSid ? { ...n, linked: false, voiceApplicationSid: null } : n
      );

      set({
        allVoiceNumbers: updatedNumbers,
        linkingNumberSid: null,
      });
      removeStorageItem('voice_numbers_cache'); // Invalidate cache
      console.log('[CallStore] Disabled incoming for number:', phoneNumberSid);
    } catch (error) {
      console.error('[CallStore] Failed to disable incoming:', error);
      set({ linkingNumberSid: null });
    }
  },

  // ── WhatsApp Voice Calling ──────────────────────────────────────

  linkWhatsAppSender: async (senderSid) => {
    const { api, twiMLAppId, whatsAppVoiceNumbers, allVoiceNumbers } = get();
    if (!api || !twiMLAppId) return;

    set({ linkingWhatsAppSenderSid: senderSid });
    try {
      const resp = await api.updateWhatsAppSenderVoiceConfig(senderSid, twiMLAppId);

      if (resp.error) {
        console.error('[CallStore] Failed to link WhatsApp sender:', resp.error);
        set({ linkingWhatsAppSenderSid: null });
        toast.error(`Failed to link WhatsApp sender: ${resp.error}`);
        return;
      }

      // Update whatsAppVoiceNumbers and rebuild callerIds
      const updatedWANumbers = whatsAppVoiceNumbers.map((n) =>
        n.sid === senderSid ? { ...n, linked: true, voiceApplicationSid: twiMLAppId } : n
      );

      const callerIds = _buildCallerIds(allVoiceNumbers, updatedWANumbers);
      removeStorageItem('voice_numbers_cache'); // Invalidate cache so reload shows the new link state

      set({
        whatsAppVoiceNumbers: updatedWANumbers,
        callerIds,
        selectedCallerId: get().selectedCallerId || (callerIds.length > 0 ? callerIds[0].value : null),
        linkingWhatsAppSenderSid: null,
      });
      console.log('[CallStore] Linked WhatsApp sender for voice:', senderSid);
    } catch (error) {
      console.error('[CallStore] Failed to link WhatsApp sender:', error);
      set({ linkingWhatsAppSenderSid: null });
      toast.error('Failed to link WhatsApp sender for voice calling');
    }
  },

  unlinkWhatsAppSender: async (senderSid) => {
    const { api, whatsAppVoiceNumbers, allVoiceNumbers, selectedCallerId } = get();
    if (!api) return;

    set({ linkingWhatsAppSenderSid: senderSid });
    try {
      const resp = await api.updateWhatsAppSenderVoiceConfig(senderSid, null);

      if (resp.error) {
        console.error('[CallStore] Failed to unlink WhatsApp sender:', resp.error);
        set({ linkingWhatsAppSenderSid: null });
        toast.error(`Failed to unlink WhatsApp sender: ${resp.error}`);
        return;
      }

      // Verify Twilio actually applied the change — the Senders API has been
      // observed accepting an update (200) without clearing the field.
      const confAfterUnlink = resp.data?.configuration;
      if (confAfterUnlink && confAfterUnlink.voice_application_sid) {
        console.error('[CallStore] Twilio did not clear voice_application_sid:', confAfterUnlink);
        set({ linkingWhatsAppSenderSid: null });
        toast.error('Twilio did not apply the unlink — please retry');
        return;
      }

      const updatedWANumbers = whatsAppVoiceNumbers.map((n) =>
        n.sid === senderSid ? { ...n, linked: false, voiceApplicationSid: null } : n
      );

      const callerIds = _buildCallerIds(allVoiceNumbers, updatedWANumbers);
      removeStorageItem('voice_numbers_cache'); // Invalidate cache so reload shows the new link state

      // Reset selected caller ID if it was the unlinked sender
      const unlinkedSender = whatsAppVoiceNumbers.find((n) => n.sid === senderSid);
      let newSelectedCallerId = selectedCallerId;
      if (unlinkedSender && selectedCallerId === unlinkedSender.phoneNumber) {
        newSelectedCallerId = callerIds.length > 0 ? callerIds[0].value : null;
      }

      set({
        whatsAppVoiceNumbers: updatedWANumbers,
        callerIds,
        selectedCallerId: newSelectedCallerId,
        linkingWhatsAppSenderSid: null,
      });
      console.log('[CallStore] Unlinked WhatsApp sender from voice:', senderSid);
    } catch (error) {
      console.error('[CallStore] Failed to unlink WhatsApp sender:', error);
      set({ linkingWhatsAppSenderSid: null });
      toast.error('Failed to unlink WhatsApp sender');
    }
  },

  requestWhatsAppCallPermission: async (contactNumber) => {
    const { api, accountSid, selectedCallerId, callerIds } = get();
    if (!api || !accountSid || !selectedCallerId) return;

    const callerObj = callerIds.find((c) => c.value === selectedCallerId);
    if (callerObj?.channel !== 'WHATSAPP') return;

    // KAN-14: this template is a real, customer-visible WhatsApp message, so it
    // must respect sender permissions like any other send. Permissions are keyed
    // by sender id while the caller id is a phone number, so map number -> sender
    // first; if no sender matches we allow (consistent with the retry gate).
    // Admins bypass every permission — skip the sender lookup entirely so
    // their call-permission requests pay no extra network round-trip.
    // Claim the slot before anything awaited. The 'pending' marker is what swaps
    // the button out of its idle state, and the permission gate below makes two
    // network round-trips, so leaving the flip until after it left the button
    // live long enough for a second click to send the template twice. Restored
    // to its previous value on every refusal below. (KAN-14)
    const priorPermissionState = get().whatsAppCallPermissions[contactNumber];
    const releaseSlot = () => set((s) => {
      const next = { ...s.whatsAppCallPermissions };
      if (priorPermissionState === undefined) delete next[contactNumber];
      else next[contactNumber] = priorPermissionState;
      return { whatsAppCallPermissions: next };
    });
    set((s) => ({ whatsAppCallPermissions: { ...s.whatsAppCallPermissions, [contactNumber]: 'pending' } }));

    const { useAuthStore } = await import('./auth-store');
    const isSharedUser = !!useAuthStore.getState().selectedAdminUserId;
    if (isSharedUser) {
      try {
        const { useModuleStore } = await import('./module-store');
        const tpa = useModuleStore.getState().tpaModule;
        if (tpa) {
          const callerDigits = selectedCallerId.replace(/\D/g, '');
          const senders = await tpa.getSenders();
          // Must be the WHATSAPP sender: getSenders lists SMS numbers first and
          // the same number is usually both, so an unfiltered match would check
          // the SMS entry's permission for a WhatsApp send. No WhatsApp sender
          // at all means the lookup failed rather than that none exists — either
          // that half of the fetch errored (each half has its own catch) or the
          // whole call did, since proxyRequest returns {success:false} instead
          // of throwing and getSenders swallows it into an empty list. We could
          // not check, and this template is a real customer-visible message.
          const waSenders = senders.filter((sn) => sn.channel === 'WHATSAPP');
          if (waSenders.length === 0) {
            toast.error("Couldn't check your permission for this number. Try again.");
            releaseSlot();
            return;
          }
          const matched = waSenders.find((sn) =>
            (sn.phoneNumber || '').replace(/\D/g, '') === callerDigits ||
            ((sn.data?.msisdn as string) || '') === callerDigits);
          if (matched) {
            const { getSenderPermissionStandalone, senderPermissionAllowsSend } =
              await import('@/hooks/use-permissions');
            if (!senderPermissionAllowsSend(getSenderPermissionStandalone(matched.id))) {
              toast.error("You don't have permission to send from this number");
              releaseSlot();
              return;
            }
          }
        }
      } catch {
        // The lookup errored rather than finding nothing. This template is a
        // real customer-visible message, so a transient failure must not grant
        // a restricted user the send — fail closed. (KAN-14)
        toast.error("Couldn't check your permission for this number. Try again.");
        releaseSlot();
        return;
      }
    }


    try {
      const fromSender = selectedCallerId.startsWith('whatsapp:') ? selectedCallerId : `whatsapp:${selectedCallerId}`;
      const toNumber = contactNumber.startsWith('whatsapp:') ? contactNumber : `whatsapp:${contactNumber}`;

      // Find or create VOICE_CALL_REQUEST content template (cached after first lookup)
      let templateSid = get()._cachedCallRequestTemplateSid;

      if (!templateSid) {
        // Check existing templates for a reusable one
        const templatesResp = await api.getContentTemplates();
        const templates = templatesResp.data?.contents || [];
        const existing = templates.find((t) => {
          const types = t.types || {};
          return Object.values(types).some((ct) =>
            ct.actions?.some((a) => a.type === 'VOICE_CALL_REQUEST')
          );
        });

        if (existing) {
          templateSid = existing.sid;
        } else {
          // Create once — no approval needed since this is sent within a conversation window
          const createResp = await api.createContentTemplate({
            friendly_name: 'ulgebra_voice_call_request',
            language: 'en',
            types: {
              'twilio/call-to-action': {
                body: 'We would like to call you. Please accept to allow the call.',
                actions: [{ type: 'VOICE_CALL_REQUEST', title: 'Accept Call Request' }],
              },
            },
          });
          if (createResp.data?.sid) {
            templateSid = createResp.data.sid;
          }
        }

        // Cache for subsequent requests
        if (templateSid) {
          set({ _cachedCallRequestTemplateSid: templateSid });
        }
      }

      if (templateSid) {
        await api.sendMessage(accountSid, {
          ContentSid: templateSid,
          From: fromSender,
          To: toNumber,
        });
        console.log('[CallStore] Sent WhatsApp call permission request to:', contactNumber);
      }
    } catch (error) {
      console.error('[CallStore] Failed to request WhatsApp call permission:', error);
      set((s) => ({
        whatsAppCallPermissions: { ...s.whatsAppCallPermissions, [contactNumber]: 'none' },
      }));
      toast.error('Failed to send WhatsApp call permission request');
    }
  },

  handleWhatsAppCallPermissionResponse: (contactNumber, accepted) => {
    set((s) => ({
      whatsAppCallPermissions: {
        ...s.whatsAppCallPermissions,
        [contactNumber]: accepted ? 'accepted' : 'rejected',
      },
    }));
    console.log('[CallStore] WhatsApp call permission response:', contactNumber, accepted ? 'ACCEPTED' : 'REJECTED');
  },

  // ── Incoming Call Agents (per caller ID) ────────────────────────

  loadIncomingCallAgents: async () => {
    const docPath = _getPersonalSettingsDocPath();
    if (!docPath) return;

    try {
      const snapshot = await getDoc(doc(db, docPath));
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      const agentMap: Record<string, string[]> = {};

      // Extract all ua_incoming_call_agents_callerId_* fields
      for (const key of Object.keys(data)) {
        if (key.startsWith('ua_incoming_call_agents_callerId_')) {
          const cleanCallerId = key.replace('ua_incoming_call_agents_callerId_', '');
          // Arrays arrive unvalidated; a non-string entry would crash the
          // case-insensitive comparisons in the availability panel and popup.
          agentMap[cleanCallerId] = Array.isArray(data[key])
            ? data[key].filter((a: unknown): a is string => typeof a === 'string')
            : [];
        }
      }

      set({ incomingCallAgents: agentMap });
      console.log('[CallStore] Loaded incoming call agents:', Object.keys(agentMap).length, 'caller IDs configured');
    } catch (error) {
      console.error('[CallStore] Failed to load incoming call agents:', error);
    }
  },

  saveIncomingCallAgents: async (callerId, agents) => {
    const docPath = _getPersonalSettingsDocPath();
    if (!docPath) return;

    const cleanCallerId = callerId.replace(/[^0-9A-Za-z]+/g, '');
    const key = `ua_incoming_call_agents_callerId_${cleanCallerId}`;

    // If no agents selected, default to current user's email
    const user = useAuthStore.getState().user;
    const effectiveAgents = agents.length === 0 && user?.email ? [user.email] : agents;

    try {
      await setDoc(doc(db, docPath), { [key]: effectiveAgents }, { merge: true });
      set({
        incomingCallAgents: {
          ...get().incomingCallAgents,
          [cleanCallerId]: effectiveAgents,
        },
      });
      console.log('[CallStore] Saved agents for', callerId, ':', effectiveAgents);
    } catch (error) {
      console.error('[CallStore] Failed to save incoming call agents:', error);
    }
  },

  // ── Ring Groups ────────────────────────────────────────────────

  fetchRingGroups: async () => {
    const uid = getEffectiveUid();
    const extensionName = useAppStore.getState().extensionName;
    if (!uid || !extensionName) return;

    set({ ringGroupsLoading: true });
    try {
      const colRef = collection(db, `ulgebraUsers/${uid}/extensions/${extensionName}/ringGroups`);
      const snapshot = await getDocs(colRef);
      const groups: RingGroup[] = [];
      snapshot.forEach((d: QueryDocumentSnapshot<DocumentData>) => {
        const g = d.data() as RingGroup;
        // Docs arrive unvalidated; a non-string agents entry would crash the
        // case-insensitive comparisons in the settings UI.
        g.agents = Array.isArray(g.agents)
          ? g.agents.filter((a): a is string => typeof a === 'string')
          : [];
        groups.push(g);
      });
      set({ ringGroups: groups, ringGroupsLoading: false });
    } catch (err) {
      console.error('[CallStore] Failed to fetch ring groups:', err);
      set({ ringGroupsLoading: false });
    }
  },

  saveRingGroup: async (group) => {
    const uid = getEffectiveUid();
    const extensionName = useAppStore.getState().extensionName;
    if (!uid || !extensionName) return false;

    try {
      await setDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/ringGroups`, group.id), group);
      set((s) => ({
        ringGroups: s.ringGroups.some((g) => g.id === group.id)
          ? s.ringGroups.map((g) => (g.id === group.id ? group : g))
          : [...s.ringGroups, group],
      }));
      return true;
    } catch (err) {
      console.error('[CallStore] Failed to save ring group:', err);
      toast.error('Could not save the ring group. Try again.');
      return false;
    }
  },

  deleteRingGroup: async (id) => {
    const uid = getEffectiveUid();
    const extensionName = useAppStore.getState().extensionName;
    if (!uid || !extensionName) return;

    try {
      await deleteDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/ringGroups`, id));
      set((s) => ({ ringGroups: s.ringGroups.filter((g) => g.id !== id) }));
    } catch (err) {
      console.error('[CallStore] Failed to delete ring group:', err);
    }
  },

  // ── Hold music ─────────────────────────────────────────────────

  _getHoldMusicUrl: async () => {
    const { holdMusicLoaded, holdMusicUrl } = get();
    if (holdMusicLoaded) return holdMusicUrl || '';

    const uid = getEffectiveUid();
    const extensionName = useAppStore.getState().extensionName;
    if (!uid || !extensionName) return '';

    try {
      const snap = await getDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/holdSettings`));
      let url = snap.exists() ? ((snap.data().musicUrl as string) || '').trim() : '';
      // Guard against junk saved before validation existed: a waitUrl Twilio
      // can't fetch makes it play an error message to the held caller and
      // drop them. Fall back to Twilio's default hold music instead.
      if (url && !isValidAudioUrl(url)) {
        console.warn('[CallStore] Ignoring invalid hold music URL:', url);
        url = '';
      }
      set({ holdMusicUrl: url, holdMusicLoaded: true });
      return url;
    } catch (err) {
      console.error('[CallStore] Failed to load hold music setting:', err);
      // Don't mark loaded — retry on the next hold
      return '';
    }
  },

  setHoldMusicUrl: (url) => set({ holdMusicUrl: url, holdMusicLoaded: true }),

  _getAutoRecordSetting: async () => {
    // Read fresh on every answered call, never from a cache: an admin turning
    // auto-record on or off must reach agents' already-open tabs right away
    // (turning it OFF is a compliance switch). One small doc read per call.
    // Same owner + path rules as the hold music setting.
    const uid = getEffectiveUid();
    const extensionName = useAppStore.getState().extensionName;
    if (!uid || !extensionName) return false;

    try {
      const snap = await getDoc(doc(db, `ulgebraUsers/${uid}/extensions/${extensionName}/callSettings/recordingSettings`));
      const on = snap.exists() && snap.data().autoRecord === true;
      set({ autoRecordCalls: on });
      return on;
    } catch (err) {
      console.error('[CallStore] Failed to load auto-record setting:', err);
      return false;
    }
  },

  setAutoRecordCalls: (on) => set({ autoRecordCalls: on }),

  // ── Agent Statuses (admin reads all agents' availability) ──────

  loadAgentStatuses: async () => {
    const docPath = _getPersonalSettingsDocPath();
    if (!docPath) return;

    set({ agentStatusesLoading: true });

    try {
      const snapshot = await getDoc(doc(db, docPath));
      if (!snapshot.exists()) {
        set({ agentStatuses: {}, agentStatusesLoading: false });
        return;
      }

      const data = snapshot.data();
      const statuses: Record<string, { available: boolean; lastSeen: number; email: string; name: string }> = {};

      for (const key of Object.keys(data)) {
        if (key.startsWith('ua_voice_agent_status_')) {
          const status = data[key];
          if (status?.email) {
            statuses[status.email] = status;
          }
        }
      }

      set({ agentStatuses: statuses, agentStatusesLoading: false });
      console.log('[CallStore] Loaded agent statuses:', Object.keys(statuses).length, 'agents');
    } catch (error) {
      console.error('[CallStore] Failed to load agent statuses:', error);
      set({ agentStatusesLoading: false });
    }
  },

  // ── Refresh Voice Config (deferred fresh calls) ────────────────

  refreshVoiceConfig: async () => {
    const { api, accountSid } = get();
    if (!api || !accountSid) {
      console.log('[CallStore] refreshVoiceConfig: skipped (no api or accountSid)');
      return;
    }

    // Use store twiMLAppId, fall back to localStorage cache
    const twiMLAppId = get().twiMLAppId || _getCachedTwiMLAppId();

    // ── Check localStorage cache (24h TTL) ──
    const cached = getStorageItem<VoiceNumbersCache | null>('voice_numbers_cache', null);
    if (cached && (Date.now() - cached.timestamp) < VOICE_NUMBERS_CACHE_TTL) {
      console.log('[CallStore] refreshVoiceConfig: using cached voice numbers');
      const callerIds = _buildCallerIds(cached.allVoiceNumbers, cached.whatsAppVoiceNumbers);
      set({
        allVoiceNumbers: cached.allVoiceNumbers,
        whatsAppVoiceNumbers: cached.whatsAppVoiceNumbers,
        callerIds,
        selectedCallerId: get().selectedCallerId || (callerIds.length > 0 ? callerIds[0].value : null),
      });
      // Still refresh agent assignments (lightweight Firestore read)
      await get().loadIncomingCallAgents();
      return;
    }

    try {
      console.log('[CallStore] refreshVoiceConfig: fetching fresh phone numbers and WhatsApp senders...');

      // Fetch phone numbers and WhatsApp senders in parallel
      const [numbersResp, waSendersResp] = await Promise.all([
        api.getIncomingPhoneNumbersUncached(accountSid),
        api.getWhatsAppSendersUncached(),
      ]);

      const numbers = numbersResp.data?.incoming_phone_numbers || [];
      const waSenders = waSendersResp.data?.senders || [];

      // ── PSTN phone numbers ──
      // All voice-capable numbers are available as caller IDs.
      // "linked" only indicates whether incoming calls are handled by our TwiML app.
      const allVoiceNumbers: VoiceNumber[] = numbers
        .filter((n) => n.capabilities?.voice)
        .map((n) => ({
          sid: n.sid,
          phoneNumber: n.phone_number,
          friendlyName: n.friendly_name,
          linked: twiMLAppId
            ? n.voice_application_sid === twiMLAppId
            : !!n.voice_application_sid,
          voiceApplicationSid: n.voice_application_sid || null,
          channel: 'SMS' as const,
        }));

      // ── WhatsApp senders with voice capability ──
      const whatsAppVoiceNumbers: VoiceNumber[] = waSenders.map((s) => ({
        sid: s.sid,
        phoneNumber: s.sender_id, // e.g. "whatsapp:+14155238886"
        friendlyName: s.profile?.name || s.sender_id,
        linked: twiMLAppId
          ? s.configuration?.voice_application_sid === twiMLAppId
          : !!s.configuration?.voice_application_sid,
        voiceApplicationSid: s.configuration?.voice_application_sid || null,
        channel: 'WHATSAPP' as const,
      }));

      // ── Cache to localStorage (24h TTL) ──
      setStorageItem('voice_numbers_cache', {
        timestamp: Date.now(),
        allVoiceNumbers,
        whatsAppVoiceNumbers,
      } satisfies VoiceNumbersCache);

      // ── Build combined caller IDs ──
      const callerIds = _buildCallerIds(allVoiceNumbers, whatsAppVoiceNumbers);

      set({
        allVoiceNumbers,
        whatsAppVoiceNumbers,
        callerIds,
        selectedCallerId: get().selectedCallerId || (callerIds.length > 0 ? callerIds[0].value : null),
      });

      console.log('[CallStore] refreshVoiceConfig: found', allVoiceNumbers.length, 'voice numbers,', whatsAppVoiceNumbers.length, 'WA senders,', callerIds.length, 'caller IDs');

      // Also refresh incoming call agent assignments
      await get().loadIncomingCallAgents();
    } catch (error) {
      console.error('[CallStore] refreshVoiceConfig failed:', error);
    }
  },

  // ── Missed Call Actions ─────────────────────────────────────────

  dismissMissedCall: (callId) => {
    set({ missedCalls: get().missedCalls.filter((c) => c.id !== callId) });
  },

  dismissAllMissedCalls: () => {
    set({ missedCalls: [] });
  },

  // ── Internal: Voice Event Handler ───────────────────────────────

  _handleVoiceEvent: (event) => {
    const { callId } = event;
    console.log('[CallStore] Voice event:', event.type, callId ? `callId:${callId}` : '', event.errorMessage || '');

    switch (event.type) {
      case 'device-registered':
        set({ softphoneState: 'registered' });
        break;

      case 'device-registering':
        set({ softphoneState: 'registering' });
        break;

      case 'device-unregistered':
        set({ softphoneState: 'unregistered' });
        break;

      case 'device-error':
        set({ softphoneState: 'error' });
        break;

      case 'device-destroyed':
        set({ softphoneState: 'unregistered' });
        break;

      case 'token-will-expire': {
        // Auto-refresh token
        const { api, twiMLAppId, voiceHandler } = get();
        if (api && twiMLAppId) {
          api.getVoiceToken(twiMLAppId).then((resp) => {
            if (resp.data?.token && voiceHandler) {
              voiceHandler.updateToken(resp.data.token);
              _setCachedVoiceToken(resp.data.token);
              console.log('[CallStore] Token refreshed and cached');
            }
          });
        }
        break;
      }

      case 'incoming-call':
        if (callId) {
          // Prevent duplicate entries for the same callId
          if (get().activeCalls.some((c) => c.id === callId)) break;
          // Auto-reject if this call was already declined/rejected
          const rejectedCall = get().activeCalls.find((c) => c.id === callId && (c.status === 'ended' || c.status === 'rejected' || c.status === 'canceled'));
          if (rejectedCall || get()._rejectedCallIds?.has(callId)) {
            get().voiceHandler?.endCall(callId);
            break;
          }
          const newCall: ActiveCall = {
            id: callId,
            tpaCallId: callId,
            direction: 'inbound',
            status: 'ringing',
            customerNumber: event.customerNumber || 'Unknown',
            companyCallerId: event.companyCallerId || '',
            startTime: null,
            duration: 0,
            isMuted: false,
            isRecording: false,
            isOnHold: false,
            callChannel: event.callChannel || 'pstn',
          };
          set((s) => ({ activeCalls: [...s.activeCalls, newCall] }));
        }
        break;

      case 'call-connecting':
        if (callId) get()._updateCallStatus(callId, 'connecting');
        break;

      case 'call-ringing':
        if (callId) get()._updateCallStatus(callId, 'ringing');
        break;

      case 'call-accepted':
        if (callId) {
          get()._updateCallStatus(callId, 'connected');
          get()._startDurationTimer(callId);
          // KAN-180: admin turned on auto-record → start recording by itself.
          void get()._getAutoRecordSetting().then(async (on) => {
            const c = get().activeCalls.find((x) => x.id === callId);
            if (!on || !c || c.status !== 'connected' || c.isRecording) return;
            await get().toggleRecording(callId);
            const after = get().activeCalls.find((x) => x.id === callId);
            if (after && !TERMINAL_CALL_STATUSES.includes(after.status) && !after.isRecording) {
              toast.error('Auto-recording could not start — press Record to try again');
            }
          });
        }
        break;

      case 'call-disconnected':
        if (callId) {
          const dcCall = get().activeCalls.find((c) => c.id === callId);
          const midTransfer = dcCall?.status === 'transferred' || dcCall?.status === 'transferring';
          // Mid-transfer the agent's leg is EXPECTED to drop while the customer
          // sits parked, and transferCall owns what happens to them next — it
          // resolves the final status and rings the agent back if the target
          // never answers. Master's held-leg cleanup HANGS UP the customer, so
          // running it here would drop the very caller the transfer is holding.
          if (!midTransfer) {
            // Agent leg died (hangup elsewhere, network, backend) — if a hold
            // was in progress, don't leave the customer waiting on hold music.
            get()._releaseHeldCustomerLeg(callId);
            get()._updateCallStatus(callId, 'ended');
          }
          get()._stopDurationTimer(callId);
        }
        break;

      case 'call-canceled':
        if (callId) {
          const canceledCall = get().activeCalls.find((c) => c.id === callId);
          // Ignore cancel for calls already connected (parent leg cancel after bridge)
          if (canceledCall && canceledCall.status === 'connected') {
            console.log('[CallStore] Ignoring cancel for already-connected call:', callId);
            break;
          }
          get()._stopDurationTimer(callId);
          // For inbound ringing calls, check if another agent answered or genuinely missed
          if (
            canceledCall &&
            canceledCall.direction === 'inbound' &&
            (canceledCall.status === 'ringing' || canceledCall.status === 'connecting')
          ) {
            // Stay NEUTRAL while verifying — 'canceled' is hidden from the
            // widget, so the user never sees a wrong "Answered by another
            // agent" flash for a call that was actually missed. Only after the
            // sibling check confirms another agent answered do we show it.
            get()._updateCallStatus(callId, 'canceled');
            const { api, accountSid } = get();
            const markMissed = () => {
              set((s) => ({
                missedCalls: [...s.missedCalls, { ...canceledCall, status: 'canceled' as CallStatus }],
              }));
            };
            const markAnsweredElsewhere = () => {
              // Un-dismiss in case the auto-dismiss already parked the id while
              // the call sat in the hidden 'canceled' state.
              set((s) => {
                const next = new Set(s.dismissedCallIds);
                next.delete(callId);
                return { dismissedCallIds: next };
              });
              get()._updateCallStatus(callId, 'answered-elsewhere');
            };
            if (api && accountSid) {
              // "Answered elsewhere" is only true when ANOTHER agent leg of the
              // same parent call is live/complete. The parent (customer) leg's own
              // status can't distinguish this — it reads in-progress/completed in
              // BOTH cases — so check the sibling legs instead. On persistent API
              // failure default to missed (master's guarantee: missed calls are
              // never lost).
              //
              // Scale: this classification runs in EVERY losing browser of a
              // fan-out, and Twilio cancels all N-1 legs within the same second
              // — N-1 browsers firing identical REST calls at once is a
              // self-inflicted 429 storm, and a 429 here used to mislabel the
              // call "missed" for that agent while others showed "answered
              // elsewhere". So: (1) spread the burst with a random 0-2.5s
              // delay — the call sits in the hidden 'canceled' state
              // meanwhile, so nothing wrong flashes — and (2) retry once
              // before accepting a failure as "missed".
              const jitter = Math.random() * 2500;
              const checkSiblings = async (): Promise<boolean | null> => {
                const resp = await api.getCallById(accountSid, callId);
                if (!resp.success) return null;                       // unknown — retry
                const parentSid = resp.data?.parent_call_sid || null;
                if (!parentSid) return false;                         // no parent — genuinely missed
                const sibResp = await api.getChildCalls(accountSid, parentSid);
                if (!sibResp.success) return null;                    // unknown — retry
                const siblings = sibResp.data?.calls || [];
                return siblings.some(
                  (c) => c.sid !== callId && (c.status === 'in-progress' || c.status === 'completed')
                );
              };
              (async () => {
                await new Promise((r) => setTimeout(r, jitter));
                let verdict = await checkSiblings().catch(() => null);
                if (verdict === null) {
                  await new Promise((r) => setTimeout(r, 2000 + Math.random() * 2000));
                  verdict = await checkSiblings().catch(() => null);
                }
                if (verdict === true) markAnsweredElsewhere();
                else markMissed();
              })();
            } else {
              markMissed();
            }
          } else {
            get()._updateCallStatus(callId, 'canceled');
          }
        }
        break;

      case 'call-rejected':
        if (callId) {
          get()._updateCallStatus(callId, 'rejected');
          get()._stopDurationTimer(callId);
        }
        break;

      case 'call-error':
        if (callId) {
          get()._updateCallStatus(callId, 'error', {
            errorMessage: event.errorMessage,
          });
          get()._stopDurationTimer(callId);
        }
        break;

      case 'call-muted':
        if (callId) {
          set({
            activeCalls: get().activeCalls.map((c) =>
              c.id === callId ? { ...c, isMuted: true } : c
            ),
          });
        }
        break;

      case 'call-unmuted':
        if (callId) {
          set({
            activeCalls: get().activeCalls.map((c) =>
              c.id === callId ? { ...c, isMuted: false } : c
            ),
          });
        }
        break;

      case 'call-id-updated': {
        const { oldCallId, newCallId } = event;
        if (oldCallId && newCallId) {
          set({
            activeCalls: get().activeCalls.map((c) =>
              c.id === oldCallId
                ? { ...c, id: newCallId, tpaCallId: newCallId }
                : c
            ),
          });
          // Transfer duration timer
          const { durationTimers } = get();
          const timer = durationTimers.get(oldCallId);
          if (timer) {
            durationTimers.delete(oldCallId);
            durationTimers.set(newCallId, timer);
          }
        }
        break;
      }
    }
  },

  // ── Internal: Update call status ────────────────────────────────

  _updateCallStatus: (callId, status, extra) => {
    set({
      activeCalls: get().activeCalls.map((c) => {
        if (c.id !== callId) return c;
        // Never resurrect a terminal call with a live status — async work
        // (e.g. an in-flight hold) may finish after the call already ended.
        const nextStatus =
          TERMINAL_CALL_STATUSES.includes(c.status) && !TERMINAL_CALL_STATUSES.includes(status)
            ? c.status
            : status;
        return { ...c, status: nextStatus, ...extra };
      }),
    });
  },

  // ── Internal: Duration Timer ────────────────────────────────────

  _startDurationTimer: (callId) => {
    const { durationTimers } = get();
    // Don't start if already running
    if (durationTimers.has(callId)) return;

    // Set startTime
    set({
      activeCalls: get().activeCalls.map((c) =>
        c.id === callId ? { ...c, startTime: Date.now() } : c
      ),
    });

    const timer = setInterval(() => {
      set({
        activeCalls: get().activeCalls.map((c) =>
          c.id === callId && c.startTime
            ? { ...c, duration: Math.floor((Date.now() - c.startTime) / 1000) }
            : c
        ),
      });
    }, 1000);

    durationTimers.set(callId, timer);
  },

  _stopDurationTimer: (callId) => {
    const { durationTimers } = get();
    const timer = durationTimers.get(callId);
    if (timer) {
      clearInterval(timer);
      durationTimers.delete(callId);
    }
  },

  // ── Internal: Discover or Create TwiML App ────────────────────

  _discoverOrCreateTwiMLApp: async (api, accountSid, webhookBaseUrl, saasServiceName, isSharedUser) => {
    try {
      const statusCallbackUrl = webhookBaseUrl.replace('_ua_action=call-add', '_ua_action=call-status');
      const appsResp = await api.getTwiMLApps(accountSid);
      const apps = appsResp.data?.applications || [];

      // Find matching TwiML app by friendly name
      const userEmail = useAuthStore.getState().user?.email || '';
      const appName = `Ulgebra Voice - ${saasServiceName} - ${userEmail}`;
      let twiMLApp = apps.find((a) => a.friendly_name === appName);

      // Fallback: match old naming format or broader match
      if (!twiMLApp) {
        twiMLApp = apps.find((a) => a.friendly_name === `Ulgebra ${saasServiceName} Calls`);
      }
      if (!twiMLApp) {
        twiMLApp = apps.find(
          (a) =>
            a.friendly_name?.toLowerCase().includes('ulgebra') &&
            a.friendly_name?.toLowerCase().includes('voice')
        );
      }
      if (!twiMLApp) {
        twiMLApp = apps.find(
          (a) =>
            a.friendly_name?.toLowerCase().includes(saasServiceName.toLowerCase()) &&
            a.friendly_name?.toLowerCase().includes('call')
        );
      }

      if (twiMLApp) {
        console.log('[CallStore] Found existing TwiML app:', twiMLApp.sid, twiMLApp.friendly_name);

        // Admin only: Check/update VoiceUrl, StatusCallback, and FriendlyName if they don't match
        const needsUpdate = !isSharedUser && (
          twiMLApp.voice_url !== webhookBaseUrl ||
          twiMLApp.status_callback !== statusCallbackUrl ||
          twiMLApp.friendly_name !== appName
        );
        if (needsUpdate) {
          console.log('[CallStore] Updating TwiML app:', appName);
          await api.updateTwiMLApp(accountSid, twiMLApp.sid, {
            FriendlyName: appName,
            VoiceUrl: webhookBaseUrl,
            StatusCallback: statusCallbackUrl,
          });
        }

        return twiMLApp.sid;
      }

      // No matching app found
      if (isSharedUser) {
        console.warn('[CallStore] Shared user: No TwiML app found, cannot create');
        set({
          softphoneState: 'error',
          voiceError: 'Voice calling is not configured. Contact your admin to set up voice.',
        });
        return null;
      }

      // Admin: Create new TwiML app
      console.log('[CallStore] Creating new TwiML app:', appName);
      const createResp = await api.createTwiMLApp(accountSid, {
        FriendlyName: appName,
        VoiceUrl: webhookBaseUrl,
        StatusCallback: statusCallbackUrl,
      });

      if (createResp.data?.sid) {
        console.log('[CallStore] Created TwiML app:', createResp.data.sid);
        return createResp.data.sid;
      }

      console.error('[CallStore] Failed to create TwiML app:', createResp.error);
      set({ softphoneState: 'error' });
      return null;
    } catch (error) {
      console.error('[CallStore] _discoverOrCreateTwiMLApp failed:', error);
      set({ softphoneState: 'error' });
      return null;
    }
  },

  // ── Internal: Register Twilio Device ──────────────────────────

  _registerDevice: async (token, extensionName) => {
    try {
      console.log('[CallStore] Step 4: Initializing Twilio Device...');
      const handler = new TwilioVoiceHandler(get()._handleVoiceEvent);
      const success = await handler.initialize(token);

      if (!success) {
        console.error('[CallStore] Step 4 FAILED: Device initialization returned false');
        set({ softphoneState: 'error' });
        return;
      }

      // Device registered — restore availability from cache
      set({ voiceHandler: handler, softphoneState: 'registered' });
      console.log('[CallStore] Step 4: Device registered successfully');

      // Notify CF with the actual availability state (not always true)
      _notifyAgentAvailability(extensionName, get().isAvailable);

      // Load incoming call agents (non-blocking)
      get().loadIncomingCallAgents().catch((err) => {
        console.warn('[CallStore] Failed to load incoming call agents:', err);
      });
    } catch (error) {
      console.error('[CallStore] _registerDevice failed:', error);
      set({ softphoneState: 'error' });
    }
  },
}));
